import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Progress } from './components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { RadioGroup, RadioGroupItem } from './components/ui/radio-group';
import { Textarea } from './components/ui/textarea';
import { toast } from 'sonner';
import { Trash2, Users, Calendar, DollarSign, UserPlus, Mail, Settings, FolderOpen, MessageCircle, BarChart3, Eye, EyeOff, Pin, Lock, ExternalLink, RefreshCw } from 'lucide-react';
import HomeworkManager from './components/HomeworkManager';
import ExerciseManager from './components/ExerciseManager';
import LoreContentManager from './components/LoreContentManager';
import WorkbookContentManager from './components/WorkbookContentManager';
import CSVImportExport from './components/CSVImportExport';
import AnnouncementManager from './components/AnnouncementManager';
import ExperimentalFeatures from './components/ExperimentalFeatures';
import EventManager from './components/EventManager';
import EbookHighlightManager from './components/EbookHighlightManager';
import BlogPostManager from './components/BlogPostManager';
import ResourcesManager from './components/ResourcesManager';
import VideoManager from './components/VideoManager';
import SisterPlatformManager from './components/SisterPlatformManager';

function Admin() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState({ total: 0, intro: 0, full_series: 0, scholarships: 0 });
  const [seatInfo, setSeatInfo] = useState({});
  const [editingSeats, setEditingSeats] = useState(false);
  const [newSeats, setNewSeats] = useState({ intro_max_seats: 30, series_max_seats: 15 });
  const [aiEnabled, setAiEnabled] = useState(false);
  const [introWaitingListEnabled, setIntroWaitingListEnabled] = useState(false);
  const [seriesWaitingListEnabled, setSeriesWaitingListEnabled] = useState(false);
  const [autoSendRegistrationEmail, setAutoSendRegistrationEmail] = useState(true);
  const [autoSendWaitingListEmail, setAutoSendWaitingListEmail] = useState(true);
  const [showIntroOnLanding, setShowIntroOnLanding] = useState(true);
  const [loading, setLoading] = useState(false);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [showNewsletterOnly, setShowNewsletterOnly] = useState(true);
  const [activeTab, setActiveTab] = useState('registrations');
  const [courseFilter, setCourseFilter] = useState('all'); // 'all', 'intro', 'full_series', 'both'
  const [reportsCourseFilter, setReportsCourseFilter] = useState('all'); // Filter for Reports tab
  
  // Onboarding email state
  const [onboardingStats, setOnboardingStats] = useState({ total_registered: 0, total_active: 0, needs_onboarding: 0 });
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [sendingMassEmail, setSendingMassEmail] = useState(false);
  const [showMassEmailConfirm, setShowMassEmailConfirm] = useState(false);
  
  // Forum Admin state
  const [forumStats, setForumStats] = useState({ total_rooms: 0, total_discussions: 0, total_posts: 0, hidden_posts: 0, recent_posts: [] });
  const [forumRooms, setForumRooms] = useState([]);
  const [forumDiscussions, setForumDiscussions] = useState([]);
  const [forumPosts, setForumPosts] = useState([]);
  const [forumAdminTab, setForumAdminTab] = useState('overview'); // overview, rooms, discussions, posts

  // SSO Integration Status
  const [ssoStatus, setSsoStatus] = useState({
    elchme: { status: 'pending', lastTest: null, message: 'Not tested yet' },
    mairy: { status: 'pending', lastTest: null, message: 'Pending integration (not billed)' }
  });
  const [testingSso, setTestingSso] = useState({ elchme: false, mairy: false });

  // Platform Connectivity Status (Production indicator)
  const [platformConnectivity, setPlatformConnectivity] = useState(null);
  const [loadingConnectivity, setLoadingConnectivity] = useState(false);

  // Fetch Platform Connectivity Status
  const fetchPlatformConnectivity = async () => {
    setLoadingConnectivity(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform-connectivity`);
      if (response.ok) {
        const data = await response.json();
        setPlatformConnectivity(data);
      }
    } catch (error) {
      console.error('Failed to fetch platform connectivity:', error);
    } finally {
      setLoadingConnectivity(false);
    }
  };

  // Fetch connectivity on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchPlatformConnectivity();
    }
  }, [isAuthenticated]);

  // Test SSO Connection
  const testSsoConnection = async (service) => {
    setTestingSso(prev => ({ ...prev, [service]: true }));
    try {
      const adminEmail = localStorage.getItem('admin_email') || 'admin@belongingsessions.com';
      
      if (service === 'elchme') {
        // Test ElchmE SSO by generating a token and verifying the endpoint
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/elchme/my-activities`, {
          headers: { 'X-User-Id': 'admin-test' }
        });
        
        if (response.ok) {
          setSsoStatus(prev => ({
            ...prev,
            elchme: {
              status: 'connected',
              lastTest: new Date().toISOString(),
              message: 'Connection successful - SSO ready'
            }
          }));
          toast.success('ElchmE SSO connection verified!');
        } else {
          throw new Error('API returned error');
        }
      } else if (service === 'mairy') {
        // Test m.ai.ry SSO
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mairy/my-conversations`, {
          headers: { 'X-User-Id': 'admin-test' }
        });
        
        if (response.ok) {
          setSsoStatus(prev => ({
            ...prev,
            mairy: {
              status: 'connected',
              lastTest: new Date().toISOString(),
              message: 'Connection successful - Pending billing setup'
            }
          }));
          toast.success('m.ai.ry SSO connection verified!');
        } else {
          throw new Error('API returned error');
        }
      }
    } catch (error) {
      console.error(`SSO test failed for ${service}:`, error);
      setSsoStatus(prev => ({
        ...prev,
        [service]: {
          status: 'error',
          lastTest: new Date().toISOString(),
          message: `Connection failed: ${error.message}`
        }
      }));
      toast.error(`${service} SSO test failed`);
    } finally {
      setTestingSso(prev => ({ ...prev, [service]: false }));
    }
  };

  // Filter registrations
  const filteredRegistrations = registrations.filter(r => {
    // Newsletter filter
    if (!showNewsletterOnly && r.attending === 'newsletter') return false;
    // Course filter
    if (courseFilter !== 'all' && r.attending !== courseFilter) return false;
    return true;
  });
  
  // Filter for Reports tab
  const reportsFilteredRegistrations = registrations.filter(r => {
    if (r.attending === 'newsletter') return false;
    if (reportsCourseFilter !== 'all' && r.attending !== reportsCourseFilter) return false;
    return true;
  });
  
  const [waitingList, setWaitingList] = useState([]);
  const [editingReg, setEditingReg] = useState(null);
  const [editForm, setEditForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    attending: 'intro',
    price_selected: '175',
    referral: '',
    payment_status: 'pending',
    payment_method: '',
    in_person: false
  });
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    attending: 'intro',
    price_selected: 'free',
    referral: '',
    payment_status: 'pending',
    payment_method: '',
    in_person: false
  });
  const [emailData, setEmailData] = useState({
    to_emails: '',
    subject: '',
    message: '',
    is_html: false
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState({
    registration: { subject: '', body: '' },
    waiting_list: { subject: '', body: '' }
  });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: 465,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    admin_notification_emails: []
  });
  const [showPassword, setShowPassword] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [emailLogFilter, setEmailLogFilter] = useState('all'); // 'all', 'success', 'failed'
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);

  const [dataLoading, setDataLoading] = useState(false);
  const [dataRetryCount, setDataRetryCount] = useState(0);

  useEffect(() => {
    if (token) {
      setIsAuthenticated(true);
      
      // Load all data with retry mechanism
      const loadAllAdminData = async () => {
        setDataLoading(true);
        try {
          await Promise.allSettled([
            fetchRegistrations(),
            fetchSeatInfo(),
            fetchContactSubmissions(),
            fetchWaitingList(),
            fetchEmailTemplates(),
            fetchSMTPSettings(),
            fetchEmailLogs(),
            fetchFeedback(),
            fetchOnboardingStats()
          ]);
        } catch (error) {
          console.error('Error loading admin data:', error);
        }
        setDataLoading(false);
      };
      
      loadAllAdminData();
      
      // Check for new activity every 30 seconds
      const interval = setInterval(() => {
        checkForNewActivity();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [token]);

  // Retry data load if registrations didn't load
  useEffect(() => {
    if (isAuthenticated && !dataLoading && dataRetryCount < 3 && registrations.length === 0) {
      const retryTimeout = setTimeout(() => {
        console.log(`Retrying admin data load (attempt ${dataRetryCount + 1})...`);
        fetchRegistrations();
        fetchSeatInfo();
        setDataRetryCount(prev => prev + 1);
      }, 2000); // Wait 2 seconds before retry
      return () => clearTimeout(retryTimeout);
    }
  }, [isAuthenticated, dataLoading, dataRetryCount, registrations.length]);

  const checkForNewActivity = async () => {
    try {
      // Check for new registrations
      const regResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (regResponse.ok) {
        const data = await regResponse.json();
        const newRegs = data.registrations.filter(r => 
          new Date(r.created_at) > new Date(Date.now() - 5*60*1000) // 5 minutes
        );
        if (newRegs.length > 0) {
          toast.success(`🎉 ${newRegs.length} new registration(s)! Check the Registrations tab.`, { duration: 8000 });
        }
      }

      // Check for new contacts
      const contactResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contact-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (contactResponse.ok) {
        const data = await contactResponse.json();
        const newContacts = data.contacts.filter(c => 
          new Date(c.created_at) > new Date(Date.now() - 5*60*1000)
        );
        if (newContacts.length > 0) {
          toast.info(`📝 ${newContacts.length} new contact submission(s)! Check Contact Submissions tab.`, { duration: 8000 });
        }
      }
    } catch (error) {
      console.error('Activity check failed');
    }
  };

  const fetchContactSubmissions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contact-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContactSubmissions(data.contacts || []);
      }
    } catch (error) {
      console.error('Failed to fetch contacts');
    }
  };

  const fetchWaitingList = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/waiting-list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWaitingList(data.waiting_list || []);
      }
    } catch (error) {
      console.error('Failed to fetch waiting list');
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const templates = {
          registration: { subject: '', body: '' },
          waiting_list: { subject: '', body: '' }
        };
        data.templates.forEach(t => {
          templates[t.template_type] = { subject: t.subject, body: t.body };
        });
        setEmailTemplates(templates);
      }
    } catch (error) {
      console.error('Failed to fetch email templates');
    }
  };

  const fetchSMTPSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSmtpSettings({
          smtp_host: data.smtp_host,
          smtp_port: data.smtp_port,
          smtp_username: data.smtp_username,
          smtp_password: '',
          smtp_from_email: data.smtp_from_email,
          smtp_from_name: data.smtp_from_name,
          admin_notification_emails: data.admin_notification_emails || []
        });
      }
    } catch (error) {
      console.error('Failed to fetch SMTP settings');
    }
  };

  const fetchEmailLogs = async () => {
    setLoadingLogs(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-logs?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmailLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Failed to fetch email logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchFeedback = async () => {
    setLoadingFeedback(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/feedback`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFeedbackList(data.feedback || []);
      }
    } catch (error) {
      console.error('Failed to fetch feedback');
    } finally {
      setLoadingFeedback(false);
    }
  };

  const fetchSeatInfo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/settings`);
      if (response.ok) {
        const data = await response.json();
        setSeatInfo(data);
        setNewSeats({
          intro_max_seats: data.intro_max_seats,
          series_max_seats: data.series_max_seats
        });
        setAiEnabled(data.ai_features_enabled || false);
        setIntroWaitingListEnabled(data.intro_waiting_list_enabled || false);
        setSeriesWaitingListEnabled(data.series_waiting_list_enabled || false);
        setAutoSendRegistrationEmail(data.auto_send_registration_email !== false);
        setAutoSendWaitingListEmail(data.auto_send_waiting_list_email !== false);
        setShowIntroOnLanding(data.show_intro_on_landing !== false);
      }
    } catch (error) {
      console.error('Failed to fetch seat info');
    }
  };

  // Forum Admin Functions
  const fetchForumStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/forum/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForumStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch forum stats');
    }
  };

  const fetchForumRooms = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/forum/rooms`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForumRooms(data.rooms || []);
      }
    } catch (error) {
      console.error('Failed to fetch forum rooms');
    }
  };

  const fetchForumDiscussions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/forum/discussions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForumDiscussions(data.discussions || []);
      }
    } catch (error) {
      console.error('Failed to fetch forum discussions');
    }
  };

  const fetchForumPosts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/forum/posts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForumPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Failed to fetch forum posts');
    }
  };

  const toggleForumItemVisibility = async (type, id) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/forum/${type}/${id}/toggle-visibility`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Refresh the appropriate list
        if (type === 'rooms') fetchForumRooms();
        else if (type === 'discussions') fetchForumDiscussions();
        else if (type === 'posts') fetchForumPosts();
      }
    } catch (error) {
      toast.error('Failed to toggle visibility');
    }
  };

  const toggleDiscussionPin = async (id) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/forum/discussions/${id}/toggle-pin`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchForumDiscussions();
      }
    } catch (error) {
      toast.error('Failed to toggle pin');
    }
  };

  const toggleDiscussionLock = async (id) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/forum/discussions/${id}/toggle-lock`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchForumDiscussions();
      }
    } catch (error) {
      toast.error('Failed to toggle lock');
    }
  };

  const deleteForumItem = async (type, id) => {
    if (!window.confirm(`Are you sure you want to permanently delete this ${type.slice(0, -1)}? This cannot be undone.`)) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/forum/${type}/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success(`${type.slice(0, -1)} deleted`);
        if (type === 'rooms') fetchForumRooms();
        else if (type === 'discussions') fetchForumDiscussions();
        else if (type === 'posts') fetchForumPosts();
        fetchForumStats();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleAI = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          intro_max_seats: seatInfo.intro_max_seats,
          series_max_seats: seatInfo.series_max_seats,
          intro_date: seatInfo.intro_date,
          intro_time: seatInfo.intro_time,
          series_dates: seatInfo.series_dates,
          ai_features_enabled: !aiEnabled,
          intro_waiting_list_enabled: introWaitingListEnabled,
          series_waiting_list_enabled: seriesWaitingListEnabled,
          auto_send_registration_email: autoSendRegistrationEmail,
          auto_send_waiting_list_email: autoSendWaitingListEmail,
          pricing_25: 25.00,
          pricing_50: 50.00,
          pricing_100: 100.00,
          pricing_150: 150.00,
          pricing_200: 200.00
        })
      });
      
      if (!response.ok) throw new Error('Update failed');
      
      setAiEnabled(!aiEnabled);
      toast.success(aiEnabled ? 'AI features disabled' : 'AI features enabled');
    } catch (error) {
      toast.error('Failed to toggle AI');
    }
  };

  const toggleIntroWaitingList = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          intro_max_seats: seatInfo.intro_max_seats,
          series_max_seats: seatInfo.series_max_seats,
          intro_date: seatInfo.intro_date,
          intro_time: seatInfo.intro_time,
          series_dates: seatInfo.series_dates,
          ai_features_enabled: aiEnabled,
          intro_waiting_list_enabled: !introWaitingListEnabled,
          series_waiting_list_enabled: seriesWaitingListEnabled,
          auto_send_registration_email: autoSendRegistrationEmail,
          auto_send_waiting_list_email: autoSendWaitingListEmail,
          pricing_25: 25.00,
          pricing_50: 50.00,
          pricing_100: 100.00,
          pricing_150: 150.00,
          pricing_200: 200.00
        })
      });
      
      if (!response.ok) throw new Error('Update failed');
      
      setIntroWaitingListEnabled(!introWaitingListEnabled);
      toast.success(introWaitingListEnabled ? 'Free Intro waiting list disabled' : 'Free Intro waiting list enabled - FULL');
    } catch (error) {
      toast.error('Failed to toggle intro waiting list');
    }
  };

  const toggleSeriesWaitingList = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          intro_max_seats: seatInfo.intro_max_seats,
          series_max_seats: seatInfo.series_max_seats,
          intro_date: seatInfo.intro_date,
          intro_time: seatInfo.intro_time,
          series_dates: seatInfo.series_dates,
          ai_features_enabled: aiEnabled,
          intro_waiting_list_enabled: introWaitingListEnabled,
          series_waiting_list_enabled: !seriesWaitingListEnabled,
          auto_send_registration_email: autoSendRegistrationEmail,
          auto_send_waiting_list_email: autoSendWaitingListEmail,
          pricing_25: 25.00,
          pricing_50: 50.00,
          pricing_100: 100.00,
          pricing_150: 150.00,
          pricing_200: 200.00
        })
      });
      
      if (!response.ok) throw new Error('Update failed');
      
      setSeriesWaitingListEnabled(!seriesWaitingListEnabled);
      toast.success(seriesWaitingListEnabled ? 'Full Series waiting list disabled' : 'Full Series waiting list enabled - FULL');
    } catch (error) {
      toast.error('Failed to toggle series waiting list');
    }
  };

  const toggleAutoRegistrationEmail = async () => {
    try {
      const newValue = !autoSendRegistrationEmail;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          intro_max_seats: seatInfo.intro_max_seats,
          series_max_seats: seatInfo.series_max_seats,
          intro_date: seatInfo.intro_date,
          intro_time: seatInfo.intro_time,
          series_dates: seatInfo.series_dates,
          ai_features_enabled: aiEnabled,
          intro_waiting_list_enabled: introWaitingListEnabled,
          series_waiting_list_enabled: seriesWaitingListEnabled,
          auto_send_registration_email: newValue,
          auto_send_waiting_list_email: autoSendWaitingListEmail,
          pricing_25: 25.00,
          pricing_50: 50.00,
          pricing_100: 100.00,
          pricing_150: 150.00,
          pricing_200: 200.00
        })
      });
      
      if (!response.ok) throw new Error('Update failed');
      
      setAutoSendRegistrationEmail(newValue);
      toast.success(newValue ? 'Registration emails will auto-send' : 'Registration emails disabled');
    } catch (error) {
      toast.error('Failed to toggle registration emails');
    }
  };

  const toggleAutoWaitingListEmail = async () => {
    try {
      const newValue = !autoSendWaitingListEmail;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          intro_max_seats: seatInfo.intro_max_seats,
          series_max_seats: seatInfo.series_max_seats,
          intro_date: seatInfo.intro_date,
          intro_time: seatInfo.intro_time,
          series_dates: seatInfo.series_dates,
          ai_features_enabled: aiEnabled,
          intro_waiting_list_enabled: introWaitingListEnabled,
          series_waiting_list_enabled: seriesWaitingListEnabled,
          auto_send_registration_email: autoSendRegistrationEmail,
          auto_send_waiting_list_email: newValue,
          pricing_25: 25.00,
          pricing_50: 50.00,
          pricing_100: 100.00,
          pricing_150: 150.00,
          pricing_200: 200.00
        })
      });
      
      if (!response.ok) throw new Error('Update failed');
      
      setAutoSendWaitingListEmail(newValue);
      toast.success(newValue ? 'Waiting list emails will auto-send' : 'Waiting list emails disabled');
    } catch (error) {
      toast.error('Failed to toggle waiting list emails');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      // Check if username looks like an email
      const isEmail = username.includes('@');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          username: isEmail ? null : username,
          email: isEmail ? username : null,
          password 
        })
      });

      if (!response.ok) throw new Error('Invalid credentials');

      const data = await response.json();
      localStorage.setItem('admin_token', data.access_token);
      localStorage.setItem('admin_username', username);
      
      // Determine admin email for SSO purposes
      let adminEmail = null;
      
      // If they logged in with email, use that
      if (isEmail) {
        adminEmail = username;
      } else {
        // Map known admin usernames to their emails
        const adminEmailMap = {
          'carrie': 'carrie@defiancesprings.com',
          'tony': 'tony@defiancesprings.com',
          'steve': 'steve@defiancesprings.com'
        };
        adminEmail = adminEmailMap[username.toLowerCase()] || 'admin@belongingsessions.com';
      }
      
      // Store admin email for SSO access
      localStorage.setItem('admin_email', adminEmail);
      
      // Also fetch admin user profile and set up seamless user dashboard access
      try {
        const userResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/profile`, {
          headers: { 
            'Authorization': `Bearer ${data.access_token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (userResponse.ok) {
          const userData = await userResponse.json();
          // Set up user credentials for seamless dashboard access
          localStorage.setItem('workbook_user_id', userData.id || userData._id);
          localStorage.setItem('workbook_user_name', userData.name || username);
          localStorage.setItem('workbook_user_email', userData.email || adminEmail);
        } else {
          // Fallback: use admin credentials as user credentials
          localStorage.setItem('workbook_user_email', adminEmail);
          localStorage.setItem('workbook_user_name', username);
        }
      } catch (error) {
        console.log('Could not fetch user profile, using fallback credentials');
        // Fallback: create credentials using admin email
        localStorage.setItem('workbook_user_email', adminEmail);
        localStorage.setItem('workbook_user_name', username);
      }
      
      setToken(data.access_token);
      setIsAuthenticated(true);
      toast.success(`Welcome back, ${username}!`);
    } catch (error) {
      toast.error('Invalid credentials');
    }
  };

  const exportToGoHighLevel = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export-ghl`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data.contacts, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gohighlevel-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success('GoHighLevel export downloaded');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const updateSeats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          intro_max_seats: newSeats.intro_max_seats,
          series_max_seats: newSeats.series_max_seats,
          intro_date: seatInfo.intro_date,
          intro_time: seatInfo.intro_time,
          series_dates: seatInfo.series_dates,
          ai_features_enabled: aiEnabled,
          intro_waiting_list_enabled: introWaitingListEnabled,
          series_waiting_list_enabled: seriesWaitingListEnabled,
          pricing_25: 25.00,
          pricing_50: 50.00,
          pricing_100: 100.00,
          pricing_150: 150.00,
          pricing_200: 200.00
        })
      });
      
      if (!response.ok) throw new Error('Update failed');
      
      await fetchSeatInfo();
      setEditingSeats(false);
      toast.success('Seat limits updated');
    } catch (error) {
      toast.error('Failed to update seats');
    }
  };

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch');

      const data = await response.json();
      setRegistrations(data.registrations);
      setStats(data.stats);
    } catch (error) {
      toast.error('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (reg) => {
    setEditingReg(reg.id);
    setEditForm({
      full_name: reg.full_name || '',
      email: reg.email || '',
      phone: reg.phone || '',
      attending: reg.attending || 'intro',
      price_selected: reg.price_selected || '175',
      referral: reg.referral || '',
      payment_status: reg.payment_status || 'pending',
      payment_method: reg.payment_method || '',
      in_person: reg.in_person || false
    });
  };

  const saveEdit = async (regId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations/${regId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) throw new Error('Update failed');

      toast.success('Registration updated!');
      setEditingReg(null);
      fetchRegistrations();
    } catch (error) {
      toast.error('Failed to update registration');
    }
  };

  const fetchOnboardingStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/onboarding-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOnboardingStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch onboarding stats:', error);
    }
  };

  const sendTestOnboardingEmail = async () => {
    setSendingTestEmail(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/send-onboarding-test`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to send test email');
      
      const data = await response.json();
      toast.success(`✅ Test email sent to ${data.recipient}! Check macastat@gmail.com inbox.`);
    } catch (error) {
      toast.error('Failed to send test email: ' + error.message);
    } finally {
      setSendingTestEmail(false);
    }
  };

  const sendMassOnboardingEmail = async () => {
    setShowMassEmailConfirm(false);
    setSendingMassEmail(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/send-onboarding-all`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to send emails');
      
      const data = await response.json();
      toast.success(`✅ Sent ${data.emails_sent} onboarding emails successfully!`);
      
      if (data.errors && data.errors.length > 0) {
        toast.warning(`${data.errors.length} emails failed to send`);
      }
      
      // Refresh stats
      fetchOnboardingStats();
    } catch (error) {
      toast.error('Failed to send mass emails: ' + error.message);
    } finally {
      setSendingMassEmail(false);
    }
  };


  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this registration?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Delete failed');

      toast.success('Registration deleted');
      fetchRegistrations();
    } catch (error) {
      toast.error('Failed to delete registration');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setIsAuthenticated(false);
    setRegistrations([]);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/registrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUserData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add user');
      }

      toast.success('User added successfully!');
      setAddUserDialogOpen(false);
      setNewUserData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        attending: 'intro',
        price_selected: '175',
        referral: '',
        payment_status: 'pending',
        payment_method: '',
        in_person: false,
        comments: ''
      });
      fetchRegistrations();
    } catch (error) {
      console.error('Add user error:', error);
      toast.error(`Failed to add user: ${error.message}`);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setSendingEmail(true);
    
    try {
      // Parse email addresses (comma or newline separated)
      const emails = emailData.to_emails
        .split(/[,\n]/)
        .map(e => e.trim())
        .filter(e => e.length > 0);
      
      if (emails.length === 0) {
        toast.error('Please enter at least one email address');
        setSendingEmail(false);
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/send-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to_emails: emails,
          subject: emailData.subject,
          message: emailData.message,
          is_html: emailData.is_html
        })
      });

      if (!response.ok) throw new Error('Failed to send email');

      const data = await response.json();
      toast.success(data.message);
      
      // Reset form
      setEmailData({
        to_emails: '',
        subject: '',
        message: '',
        is_html: false
      });
      setSelectedUsers([]);
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const toggleUserSelection = (email) => {
    setSelectedUsers(prev => {
      if (prev.includes(email)) {
        return prev.filter(e => e !== email);
      } else {
        return [...prev, email];
      }
    });
  };

  const selectAllUsers = () => {
    if (!registrations || registrations.length === 0) {
      return;
    }
    if (selectedUsers.length === registrations.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(registrations.map(r => r.email));
    }
  };

  const emailSelected = () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    
    // Pre-fill email addresses
    setEmailData(prev => ({
      ...prev,
      to_emails: selectedUsers.join('\n')
    }));
    
    toast.success(`${selectedUsers.length} email(s) added. Switch to 'Send Email' tab to compose message.`);
  };

  const saveEmailTemplate = async (templateType) => {
    try {
      const template = emailTemplates[templateType];
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-templates/${templateType}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          template_type: templateType,
          subject: template.subject,
          body: template.body
        })
      });

      if (!response.ok) throw new Error('Failed to save template');

      toast.success('Email template saved!');
      setEditingTemplate(null);
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const saveSMTPSettings = async () => {
    try {
      // Convert admin emails string to array
      const emailList = typeof smtpSettings.admin_notification_emails === 'string' 
        ? smtpSettings.admin_notification_emails.split(',').map(e => e.trim()).filter(e => e)
        : smtpSettings.admin_notification_emails;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...smtpSettings,
          admin_notification_emails: emailList
        })
      });

      if (!response.ok) throw new Error('Failed to save SMTP settings');

      toast.success('SMTP settings saved! Emails will now use these credentials.');
      setShowPassword(false);
    } catch (error) {
      toast.error('Failed to save SMTP settings');
    }
  };

  const exportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Session', 'Price', 'Referral', 'Date'];
    const rows = registrations.map(r => [
      r.full_name || '',
      r.email || '',
      r.phone || '',
      r.attending || 'intro',
      r.price_selected || '175',
      r.referral || '',
      new Date(r.created_at).toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `belonging-sessions-registrations-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV exported');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        {/* Background texture */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(212, 175, 55, 0.4) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <Card className="w-full max-w-md bg-stone-900 border border-amber-500/30 relative z-10" style={{ borderRadius: 0 }}>
          <CardHeader className="border-b border-stone-800">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-700 rounded-lg flex items-center justify-center">
                <span className="text-black font-bold text-lg">B</span>
              </div>
              <div>
                <CardTitle className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Admin Login</CardTitle>
                <p className="text-amber-500/70 text-xs tracking-wide">THE BELONGING SESSIONS</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-stone-400">Username</Label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="username or email"
                  required
                  style={{ borderRadius: 0 }}
                  className="bg-stone-800/50 border-stone-700 text-white placeholder:text-stone-500 focus:border-amber-500"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-400">Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{ borderRadius: 0 }}
                  className="bg-stone-800/50 border-stone-700 text-white placeholder:text-stone-500 focus:border-amber-500"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-black font-semibold py-6"
                style={{ borderRadius: 0 }}
              >
                Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Sidebar navigation items grouped by category
  const sidebarNavItems = [
    { group: 'ATTENDEES', items: [
      { value: 'registrations', icon: '👥', label: 'Registrations', badge: registrations.filter(r => new Date(r.created_at) > new Date(Date.now() - 24*60*60*1000)).length },
      { value: 'seats', icon: '💺', label: 'Seats' },
      { value: 'contacts', icon: '📝', label: 'Contacts', badge: contactSubmissions.filter(c => new Date(c.created_at) > new Date(Date.now() - 24*60*60*1000)).length },
    ]},
    { group: 'EVENTS', items: [
      { value: 'events', icon: '📅', label: 'Events' },
      { value: 'announcements', icon: '📢', label: 'Announcements' },
    ]},
    { group: 'COMMUNITY', items: [
      { value: 'forum-admin', icon: '💬', label: 'Forum Admin' },
      { value: 'messages', icon: '✉️', label: 'Messages' },
      { value: 'email', icon: '📧', label: 'Email' },
    ]},
    { group: 'CONTENT', items: [
      { value: 'lore', icon: '📚', label: 'LORE Pages' },
      { value: 'ebook', icon: '📖', label: 'eBook' },
      { value: 'blog', icon: '📝', label: 'Blog' },
      { value: 'resources', icon: '📁', label: 'Resources' },
      { value: 'videos', icon: '🎬', label: 'Videos' },
    ]},
    { group: 'INTEGRATIONS', items: [
      { value: 'sister-platforms', icon: '🔗', label: 'Sister Platforms' },
      { value: 'research-portal', icon: '🔬', label: 'Research Portal' },
    ]},
    { group: 'ANALYTICS', items: [
      { value: 'reports', icon: '📊', label: 'Reports' },
    ]},
    { group: 'SYSTEM', items: [
      { value: 'experimental', icon: '🔬', label: 'Lab' },
      { value: 'settings', icon: '⚙️', label: 'Settings' },
    ]},
  ];

  return (
    <div className="min-h-screen bg-black text-white dark-admin">
      {/* Background texture */}
      <div className="fixed inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(212, 175, 55, 0.4) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>
      
      {/* Single Tabs Wrapper for Entire Admin */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full min-h-screen relative z-10">

      {/* Eventbrite-Style Layout: Fixed Sidebar + Main Content */}
      <div className="flex min-h-screen">
        
        {/* Fixed Left Sidebar */}
        <aside className="w-56 bg-stone-950 border-r border-stone-800 flex flex-col fixed left-0 top-0 bottom-0 z-20">
          {/* Logo Header */}
          <div className="p-4 border-b border-stone-800">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-amber-700 rounded flex items-center justify-center">
                <span className="text-black font-bold text-sm">B</span>
              </div>
              <div>
                <h1 className="text-sm font-medium text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Belonging Sessions</h1>
                <p className="text-[10px] text-stone-500">Admin Dashboard</p>
              </div>
            </div>
            
            {/* Quick Access Platform Links */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-800/50">
              <button
                onClick={() => navigate('/belonging-ebook')}
                className="group flex items-center gap-1.5 px-2 py-1.5 bg-stone-800/30 hover:bg-stone-700/50 rounded transition-colors"
                title="Your Personal E-book"
              >
                <svg className="w-4 h-4 text-amber-500/70 group-hover:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <span className="text-[10px] text-stone-400 group-hover:text-white">E-book</span>
              </button>
              
              <button
                onClick={() => {
                  // Ensure user credentials are set, then navigate
                  const userId = localStorage.getItem('workbook_user_id');
                  if (!userId) {
                    // Set up admin as user if not already done
                    const adminUsername = localStorage.getItem('admin_username') || 'Admin';
                    const adminEmail = localStorage.getItem('admin_email') || 'admin@belongingsessions.com';
                    localStorage.setItem('workbook_user_id', 'admin-' + adminUsername);
                    localStorage.setItem('workbook_user_name', adminUsername);
                    localStorage.setItem('workbook_user_email', adminEmail);
                  }
                  navigate('/dashboard');
                }}
                className="group flex items-center gap-1.5 px-2 py-1.5 bg-stone-800/30 hover:bg-stone-700/50 rounded transition-colors"
                title="View as User"
              >
                <svg className="w-4 h-4 text-emerald-600/70 group-hover:text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="text-[10px] text-stone-400 group-hover:text-white">User</span>
              </button>
              
              <button
                onClick={() => window.open('https://elchme.com', '_blank')}
                className="group flex items-center gap-1.5 px-2 py-1.5 bg-stone-800/30 hover:bg-stone-700/50 rounded transition-colors"
                title="ElchmE Main Site"
              >
                <svg className="w-4 h-4 text-purple-500/70 group-hover:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span className="text-[10px] text-stone-400 group-hover:text-white">ElchmE</span>
              </button>
              
              <button
                onClick={async () => {
                  // Generate SSO link to research portal with admin email
                  try {
                    const adminEmail = localStorage.getItem('admin_email') || 'admin@belongingsessions.com';
                    const adminUsername = localStorage.getItem('admin_username') || 'Admin';
                    
                    const { generateElchmEResearchLink } = await import('./utils/elchmeSSO');
                    const researchUrl = await generateElchmEResearchLink({ 
                      email: adminEmail, 
                      name: adminUsername 
                    });
                    
                    window.open(researchUrl, '_blank');
                    // Show success message with email being used
                    const { toast } = await import('sonner');
                    toast.success(`Opening Research Portal as ${adminEmail}`);
                  } catch (error) {
                    console.error('Failed to generate research link:', error);
                    window.open('https://elchme.com/research', '_blank');
                    const { toast } = await import('sonner');
                    toast.error('SSO failed, opening research portal directly');
                  }
                }}
                className="group flex items-center gap-1.5 px-2 py-1.5 bg-stone-800/30 hover:bg-stone-700/50 rounded transition-colors"
                title="ElchmE Research Portal with SSO"
              >
                <svg className="w-4 h-4 text-blue-500/70 group-hover:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                <span className="text-[10px] text-stone-400 group-hover:text-white">Research</span>
              </button>
              
              <button
                onClick={() => window.open('https://mairy.io', '_blank')}
                className="group flex items-center gap-1.5 px-2 py-1.5 bg-stone-800/30 hover:bg-stone-700/50 rounded transition-colors"
                title="M.ai.ry AI Wisdom"
              >
                <svg className="w-4 h-4 text-cyan-500/70 group-hover:text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className="text-[10px] text-stone-400 group-hover:text-white">M.ai.ry</span>
              </button>
              
              <button
                onClick={() => {
                  // Try primary URL, with fallback logic
                  const primaryUrl = 'https://thriveforge.emergent.host';
                  const fallbackUrl = 'https://blissabode.org';
                  
                  // Open primary, set up fallback check
                  const win = window.open(primaryUrl, '_blank');
                  
                  // If primary fails to open, try fallback after short delay
                  setTimeout(() => {
                    if (!win || win.closed || typeof win.closed === 'undefined') {
                      window.open(fallbackUrl, '_blank');
                    }
                  }, 1000);
                }}
                className="group flex items-center gap-1.5 px-2 py-1.5 bg-stone-800/30 hover:bg-stone-700/50 rounded transition-colors"
                title="BlissAbode Platform"
              >
                <svg className="w-4 h-4 text-emerald-500/70 group-hover:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span className="text-[10px] text-stone-400 group-hover:text-white">BlissAbode</span>
              </button>
            </div>
          </div>

          {/* Navigation Items */}
          <nav className="flex-1 overflow-y-auto py-2">
            <TabsList className="flex flex-col w-full bg-transparent gap-0 h-auto p-0">
              {sidebarNavItems.map((group) => (
                <div key={group.group} className="mb-2">
                  <div className="px-4 py-1.5 text-[10px] font-semibold text-stone-600 uppercase tracking-wider">
                    {group.group}
                  </div>
                  {group.items.map((item) => (
                    <TabsTrigger
                      key={item.value}
                      value={item.value}
                      className="w-full justify-start px-4 py-2 text-sm font-normal text-stone-400 hover:text-white hover:bg-stone-800/50 data-[state=active]:text-amber-500 data-[state=active]:bg-amber-500/10 data-[state=active]:border-l-2 data-[state=active]:border-amber-500 rounded-none transition-all relative"
                    >
                      <span className="mr-2">{item.icon}</span>
                      {item.label}
                      {item.badge > 0 && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 bg-amber-500 text-black text-[10px] font-bold rounded-full flex items-center justify-center">
                          {item.badge}
                        </span>
                      )}
                    </TabsTrigger>
                  ))}
                </div>
              ))}
            </TabsList>
          </nav>

          {/* Quick Stats at Bottom */}
          <div className="p-3 border-t border-stone-800 bg-stone-900/50">
            <div className="grid grid-cols-2 gap-2 text-[10px]">
              <div className="bg-stone-800/50 rounded p-2 text-center">
                <div className="text-amber-500 font-bold text-lg">{registrations.length}</div>
                <div className="text-stone-500">Total Reg</div>
              </div>
              <div className="bg-stone-800/50 rounded p-2 text-center">
                <div className="text-stone-300 font-bold text-lg">{contactSubmissions.length}</div>
                <div className="text-stone-500">Contacts</div>
              </div>
            </div>
          </div>

          {/* User Actions */}
          <div className="p-3 border-t border-stone-800">
            <div className="flex items-center justify-between">
              <Button 
                onClick={() => {
                  localStorage.setItem('workbook_user_id', 'admin-preview-' + Date.now());
                  localStorage.setItem('workbook_user_name', localStorage.getItem('admin_username') || 'Admin Preview');
                  localStorage.setItem('workbook_user_email', 'admin@preview.local');
                  window.open('/dashboard', '_blank');
                }}
                variant="ghost" 
                size="sm" 
                className="text-amber-500 hover:bg-amber-500/10 h-7 px-2 text-xs"
                title="Preview as Student"
              >
                <Eye className="h-3 w-3 mr-1" />
                View
              </Button>
              <a href="/forum" target="_blank">
                <Button variant="ghost" size="sm" className="text-teal-400 hover:text-teal-300 hover:bg-teal-500/10 h-7 px-2 text-xs" title="Community Forum">
                  <MessageCircle className="h-3 w-3" />
                </Button>
              </a>
              <a href="/" target="_blank">
                <Button variant="ghost" size="sm" className="text-stone-400 hover:text-amber-500 h-7 w-7 p-0">
                  🏠
                </Button>
              </a>
              <Button onClick={handleLogout} variant="ghost" size="sm" className="text-red-400/70 hover:text-red-400 h-7 px-2 text-xs">
                Exit
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Content Area - with left margin for sidebar */}
        <main className="flex-1 ml-56">
          {/* Compact Top Bar */}
          <header className="bg-stone-900/80 border-b border-stone-800 sticky top-0 z-10">
            <div className="px-6 py-3 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-light text-white capitalize" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {activeTab === 'registrations' ? 'Registrations' : 
                   activeTab === 'seats' ? 'Seat Management' :
                   activeTab === 'events' ? 'Events' :
                   activeTab === 'contacts' ? 'Contact Submissions' :
                   activeTab === 'messages' ? 'Messages & Feedback' :
                   activeTab === 'email' ? 'Email Center' :
                   activeTab === 'lore' ? 'LORE Content' :
                   activeTab === 'ebook' ? 'eBook Highlights' :
                   activeTab === 'announcements' ? 'Announcements' :
                   activeTab === 'blog' ? 'Blog Posts' :
                   activeTab === 'resources' ? 'Resources' :
                   activeTab === 'videos' ? 'Video Library' :
                   activeTab === 'reports' ? 'Reports & Analytics' :
                   activeTab === 'experimental' ? 'Experimental Lab' :
                   activeTab === 'settings' ? 'Settings' : activeTab}
                </h2>
              </div>
              <div className="flex items-center gap-4 text-xs text-stone-500">
                <span>{registrations.filter(r => r.attending === 'intro').length} intro</span>
                <span>{registrations.filter(r => r.attending === 'full_series').length} series</span>
              </div>
            </div>
          </header>

          {/* Tab Content Area */}
          <section className="p-6">
          
          {/* Seats Tab Content */}
          <TabsContent value="seats" className="mt-0">
            <div className="bg-stone-900/80 rounded-xl border border-stone-800 p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Seat Availability</h2>
                  <p className="text-sm text-stone-500">Manage capacity for your sessions</p>
                </div>
                <div className="flex gap-3 flex-wrap">
                  <Button 
                    onClick={toggleIntroWaitingList}
                    variant={introWaitingListEnabled ? 'default' : 'outline'}
                    className={introWaitingListEnabled ? 'bg-red-500/80 text-white hover:bg-red-500' : 'border-stone-600 text-stone-300 hover:border-amber-500/50 hover:text-amber-500'}
                    size="sm"
                  >
                    {introWaitingListEnabled ? 'Free Intro: FULL' : 'Free Intro: OPEN'}
                  </Button>
                  <Button 
                    onClick={toggleSeriesWaitingList}
                    variant={seriesWaitingListEnabled ? 'default' : 'outline'}
                    className={seriesWaitingListEnabled ? 'bg-red-500/80 text-white hover:bg-red-500' : 'border-stone-600 text-stone-300 hover:border-amber-500/50 hover:text-amber-500'}
                    size="sm"
                  >
                    {seriesWaitingListEnabled ? 'Full Series: FULL' : 'Full Series: OPEN'}
                  </Button>
                  <Button 
                    onClick={() => setEditingSeats(!editingSeats)}
                    variant="outline"
                    className="border-stone-600 text-stone-300 hover:border-amber-500/50 hover:text-amber-500"
                    size="sm"
                  >
                    {editingSeats ? 'Cancel' : 'Edit Limits'}
                  </Button>
                </div>
              </div>
              
              {editingSeats ? (
                <Card className="bg-stone-800/50 border-stone-700">
                  <CardContent className="p-6 space-y-4">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-stone-400">Free Intro Max Seats</Label>
                        <Input
                          type="number"
                          value={newSeats.intro_max_seats}
                          onChange={(e) => setNewSeats({...newSeats, intro_max_seats: parseInt(e.target.value)})}
                          className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-stone-400">Eight-Part Series Max Seats</Label>
                        <Input
                          type="number"
                          value={newSeats.series_max_seats}
                          onChange={(e) => setNewSeats({...newSeats, series_max_seats: parseInt(e.target.value)})}
                          className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                        />
                      </div>
                    </div>
                    <Button 
                      onClick={updateSeats}
                      className="bg-gradient-to-r from-amber-600 to-amber-700 text-black hover:from-amber-500 hover:to-amber-600 font-semibold"
                    >
                      Save Changes
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-stone-800 to-stone-900 rounded-xl p-6 border border-amber-500/30">
                    <h3 className="text-lg font-light text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Free Introduction</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-stone-400">Filled</span>
                        <span className="text-2xl font-light text-amber-500">{seatInfo.intro_filled} / {seatInfo.intro_max_seats}</span>
                      </div>
                      <div className="h-2 bg-stone-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-amber-600 to-amber-500 transition-all"
                          style={{ width: `${seatInfo.intro_percent_filled || 0}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-stone-500">{seatInfo.intro_available} available</span>
                        <span className="text-amber-500/70 font-medium">{seatInfo.intro_percent_filled}% full</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-stone-800 to-stone-900 rounded-xl p-6 border border-stone-700">
                    <h3 className="text-lg font-light text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Eight-Part Series</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-stone-400">Filled</span>
                        <span className="text-2xl font-light text-stone-300">{seatInfo.series_filled} / {seatInfo.series_max_seats}</span>
                      </div>
                      <div className="h-2 bg-stone-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-stone-500 to-stone-400 transition-all"
                          style={{ width: `${seatInfo.series_percent_filled || 0}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-stone-500">{seatInfo.series_available} available</span>
                        <span className="text-stone-400 font-medium">{seatInfo.series_percent_filled}% full</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Events Tab Content */}
          <TabsContent value="events" className="mt-0">
            <div className="bg-stone-900/80 rounded-xl border border-stone-800 p-6">
              <EventManager registrations={registrations} />
            </div>
          </TabsContent>



          {/* Registrations Tab */}
          <TabsContent value="registrations">
              <div>
                {/* Eventbrite-Style Quick Stats Bar */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-gradient-to-br from-teal-900/40 to-teal-800/20 border border-teal-500/30 rounded-lg p-4">
                    <p className="text-teal-400 text-xs font-medium uppercase tracking-wider">Total Attendees</p>
                    <p className="text-2xl font-bold text-white mt-1">{registrations.filter(r => r.attending !== 'newsletter').length}</p>
                    <p className="text-teal-500/70 text-xs mt-1">+{registrations.filter(r => new Date(r.created_at) > new Date(Date.now() - 7*24*60*60*1000) && r.attending !== 'newsletter').length} this week</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-900/40 to-amber-800/20 border border-amber-500/30 rounded-lg p-4">
                    <p className="text-amber-400 text-xs font-medium uppercase tracking-wider">Paid</p>
                    <p className="text-2xl font-bold text-white mt-1">{registrations.filter(r => r.payment_status === 'paid').length}</p>
                    <p className="text-amber-500/70 text-xs mt-1">${registrations.filter(r => r.payment_status === 'paid').reduce((sum, r) => sum + (parseInt(r.price_selected) || 0), 0)} collected</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 border border-purple-500/30 rounded-lg p-4">
                    <p className="text-purple-400 text-xs font-medium uppercase tracking-wider">Pending</p>
                    <p className="text-2xl font-bold text-white mt-1">{registrations.filter(r => r.payment_status === 'pending').length}</p>
                    <p className="text-purple-500/70 text-xs mt-1">awaiting payment</p>
                  </div>
                  <div className="bg-gradient-to-br from-stone-800/40 to-stone-700/20 border border-stone-600/30 rounded-lg p-4">
                    <p className="text-stone-400 text-xs font-medium uppercase tracking-wider">Newsletter Only</p>
                    <p className="text-2xl font-bold text-white mt-1">{registrations.filter(r => r.attending === 'newsletter').length}</p>
                    <p className="text-stone-500/70 text-xs mt-1">subscribers</p>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
                <div>
                  <h2 className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Registrations ({filteredRegistrations.length})</h2>
                  <p className="text-sm text-stone-500">
                    Total: {registrations.length} | 
                    Newsletter Only: {registrations.filter(r => r.attending === 'newsletter').length}
                  </p>
                </div>
                
                {/* Eventbrite-Style Action Toolbar */}
                <div className="flex flex-wrap gap-2">
                  {/* Course Filter Buttons */}
                  <div className="flex rounded overflow-hidden border border-stone-700">
                    <button
                      onClick={() => setCourseFilter('all')}
                      className={`px-2 py-1 text-xs transition-all ${
                        courseFilter === 'all' 
                          ? 'bg-amber-500 text-black' 
                          : 'bg-stone-800 text-stone-400 hover:bg-stone-700'
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setCourseFilter('intro')}
                      className={`px-2 py-1 text-xs border-l border-stone-700 transition-all ${
                        courseFilter === 'intro' 
                          ? 'bg-blue-500 text-white' 
                          : 'bg-stone-800 text-stone-400 hover:bg-stone-700'
                      }`}
                    >
                      Intro
                    </button>
                    <button
                      onClick={() => setCourseFilter('full_series')}
                      className={`px-2 py-1 text-xs border-l border-stone-700 transition-all ${
                        courseFilter === 'full_series' 
                          ? 'bg-purple-500 text-white' 
                          : 'bg-stone-800 text-stone-400 hover:bg-stone-700'
                      }`}
                    >
                      Series
                    </button>
                    <button
                      onClick={() => setCourseFilter('both')}
                      className={`px-2 py-1 text-xs border-l border-stone-700 transition-all ${
                        courseFilter === 'both' 
                          ? 'bg-emerald-500 text-white' 
                          : 'bg-stone-800 text-stone-400 hover:bg-stone-700'
                      }`}
                    >
                      Both
                    </button>
                  </div>
                  
                  <Button
                    onClick={() => setShowNewsletterOnly(!showNewsletterOnly)}
                    variant={showNewsletterOnly ? "default" : "outline"}
                    size="sm"
                    className={showNewsletterOnly ? "bg-amber-600 text-black hover:bg-amber-500" : "border-stone-600 text-stone-300 hover:border-amber-500/50"}
                    style={{ borderRadius: 0 }}
                  >
                    {showNewsletterOnly ? '📧 All' : '🚫 Hide Newsletter'}
                  </Button>
                  
                  {/* Account Management Dropdown */}
                  <div className="relative group">
                    <Button 
                      size="sm"
                      className="bg-amber-600/80 text-black hover:bg-amber-500"
                      style={{ borderRadius: 0 }}
                    >
                      👥 Accounts ▾
                    </Button>
                    <div className="absolute top-full left-0 mt-1 bg-stone-800 border border-stone-600 rounded shadow-xl z-50 min-w-[220px] hidden group-hover:block">
                      <button 
                        onClick={async () => {
                          if (selectedUsers.length === 0) {
                            toast.error('Please select users first');
                            return;
                          }
                          if (window.confirm(`Create/refresh accounts for ${selectedUsers.length} selected user(s)? Password will be set to "password"`)) {
                            try {
                              const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
                              const response = await fetch(`${BACKEND_URL}/api/admin/create-selected-accounts`, {
                                method: 'POST',
                                headers: { 
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                  emails: selectedUsers,
                                  refresh_existing: true,
                                  send_email: true
                                })
                              });
                              const data = await response.json();
                              if (response.ok) {
                                toast.success(`Created ${data.created}, refreshed ${data.refreshed} accounts!`);
                                if (data.errors?.length > 0) {
                                  console.log('Account errors:', data.errors);
                                }
                              } else {
                                toast.error(data.detail || 'Failed to create accounts');
                              }
                            } catch (error) {
                              console.error('Account creation error:', error);
                              toast.error('Connection error - please try again');
                            }
                          }
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-white hover:bg-stone-700 flex items-center gap-2"
                      >
                        <UserPlus className="w-4 h-4" />
                        Create Selected ({selectedUsers.length})
                      </button>
                      
                      <button 
                        onClick={async () => {
                          if (selectedUsers.length === 0) {
                            toast.error('Please select users first');
                            return;
                          }
                          if (window.confirm(`Create accounts for ${selectedUsers.length} user(s) AND send welcome email?`)) {
                            try {
                              const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
                              const response = await fetch(`${BACKEND_URL}/api/admin/create-selected-accounts`, {
                                method: 'POST',
                                headers: { 
                                  'Authorization': `Bearer ${token}`,
                                  'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                  emails: selectedUsers,
                                  refresh_existing: true,
                                  send_email: true
                                })
                              });
                              const data = await response.json();
                              if (response.ok) {
                                toast.success(`Processed ${data.total_processed} accounts with emails sent!`);
                              } else {
                                toast.error(data.detail || 'Failed');
                              }
                            } catch (error) {
                              toast.error('Connection error');
                            }
                          }
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-white hover:bg-stone-700 flex items-center gap-2"
                      >
                        <Mail className="w-4 h-4" />
                        Create + Send Email ({selectedUsers.length})
                      </button>
                      
                      <div className="border-t border-stone-600 my-1"></div>
                      
                      <button 
                        onClick={async () => {
                          if (window.confirm('Create accounts for ALL registrations without accounts?')) {
                            try {
                              const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
                              const response = await fetch(`${BACKEND_URL}/api/admin/create-all-accounts`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              const data = await response.json();
                              if (response.ok) {
                                toast.success(`Created ${data.created} accounts!`);
                                if (data.errors?.length > 0) {
                                  toast.warning(`${data.errors.length} errors occurred`);
                                }
                              } else {
                                toast.error(data.detail || 'Failed');
                              }
                            } catch (error) {
                              toast.error('Connection error - database may be unreachable');
                            }
                          }
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-amber-400 hover:bg-stone-700 flex items-center gap-2"
                      >
                        <UserPlus className="w-4 h-4" />
                        Create ALL Accounts
                      </button>
                    </div>
                  </div>

                  {selectedUsers.length > 0 && (
                    <Button 
                      onClick={emailSelected}
                      size="sm"
                      className="bg-teal-600 text-white hover:bg-teal-500"
                      style={{ borderRadius: 0 }}
                    >
                      <Mail className="w-3 h-3 mr-1" />
                      Email ({selectedUsers.length})
                    </Button>
                  )}
                  <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        size="sm"
                        className="bg-gradient-to-r from-amber-600 to-amber-700 text-black hover:from-amber-500 hover:to-amber-600 font-semibold"
                        style={{ borderRadius: 0 }}
                      >
                        <UserPlus className="w-3 h-3 mr-1" />
                        Add User
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto bg-stone-900 border border-stone-700 text-white" style={{ borderRadius: 0 }}>
                      <DialogHeader>
                        <DialogTitle className="text-2xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>Add New User</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleAddUser} className="space-y-6 mt-6">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-stone-400">First Name *</Label>
                            <Input
                              required
                              value={newUserData.first_name}
                              onChange={(e) => setNewUserData(prev => ({ ...prev, first_name: e.target.value }))}
                              className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                              style={{ borderRadius: 0 }}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-stone-400">Last Name *</Label>
                            <Input
                              required
                              value={newUserData.last_name}
                              onChange={(e) => setNewUserData(prev => ({ ...prev, last_name: e.target.value }))}
                              className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                              style={{ borderRadius: 0 }}
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-stone-400">Email *</Label>
                          <Input
                            type="email"
                            required
                            value={newUserData.email}
                            onChange={(e) => setNewUserData(prev => ({ ...prev, email: e.target.value }))}
                            className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-stone-400">Phone</Label>
                          <Input
                            type="tel"
                            value={newUserData.phone}
                            onChange={(e) => setNewUserData(prev => ({ ...prev, phone: e.target.value }))}
                            className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        <div className="space-y-3">
                          <Label>Attending:</Label>
                          <RadioGroup value={newUserData.attending} onValueChange={(value) => setNewUserData(prev => ({ ...prev, attending: value }))}>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="intro" id="add-intro" />
                              <Label htmlFor="add-intro">Free Introduction</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="full_series" id="add-series" />
                              <Label htmlFor="add-series">Eight-Part Series</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="both" id="add-both" />
                              <Label htmlFor="add-both">Both</Label>
                            </div>
                          </RadioGroup>
                        </div>
                        <div className="space-y-3">
                          <Label>Price:</Label>
                          <RadioGroup value={newUserData.price_selected} onValueChange={(value) => setNewUserData(prev => ({ ...prev, price_selected: value }))}>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="free" id="add-free" />
                              <Label htmlFor="add-free">Free (Intro Only)</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="175" id="add-175" />
                              <Label htmlFor="add-175">$175</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="scholarship" id="add-scholarship" />
                              <Label htmlFor="add-scholarship">Scholarship</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="pay_forward" id="add-pay-forward" />
                              <Label htmlFor="add-pay-forward">Pay Forward</Label>
                            </div>
                          </RadioGroup>
                        </div>
                        <div className="space-y-3">
                          <Label>Payment Status:</Label>
                          <RadioGroup value={newUserData.payment_status} onValueChange={(value) => setNewUserData(prev => ({ ...prev, payment_status: value }))}>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="pending" id="add-pending" />
                              <Label htmlFor="add-pending">Pending</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="paid" id="add-paid" />
                              <Label htmlFor="add-paid">Paid</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="free" id="add-status-free" />
                              <Label htmlFor="add-status-free">Free</Label>
                            </div>
                          </RadioGroup>
                        </div>
                        <div className="space-y-2">
                          <Label>Payment Method</Label>
                          <select
                            value={newUserData.payment_method}
                            onChange={(e) => setNewUserData(prev => ({ ...prev, payment_method: e.target.value }))}
                            className="w-full p-3 border border-stone-700 bg-stone-800 text-white"
                            style={{ borderRadius: 0 }}
                          >
                            <option value="">Not Set</option>
                            <option value="paypal">PayPal</option>
                            <option value="cash">Cash</option>
                            <option value="check">Check</option>
                            <option value="credit_card">Credit Card</option>
                            <option value="free">Free (Intro)</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label>Referral</Label>
                          <Input
                            value={newUserData.referral}
                            onChange={(e) => setNewUserData(prev => ({ ...prev, referral: e.target.value }))}
                            className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                            style={{ borderRadius: 0 }}
                          />
                        </div>
                        <div className="flex items-center space-x-3 p-4 bg-stone-800/50 rounded">
                          <input
                            type="checkbox"
                            id="add-in-person"
                            checked={newUserData.in_person}
                            onChange={(e) => setNewUserData(prev => ({ ...prev, in_person: e.target.checked }))}
                            className="cursor-pointer"
                          />
                          <Label htmlFor="add-in-person" className="font-normal cursor-pointer">
                            Attending IN-PERSON at Defiance Springs
                          </Label>
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full bg-amber-600 text-black hover:bg-black/90 py-6 text-lg"
                          style={{ borderRadius: 0 }}
                        >
                          Add User
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                  <Button 
                    onClick={fetchRegistrations} 
                    variant="outline"
                    size="sm"
                    className="bg-stone-800 border-stone-700 text-white hover:border-amber-500/50"
                    style={{ borderRadius: 0 }}
                    disabled={loading}
                  >
                    {loading ? '⏳' : '🔄'} Refresh
                  </Button>
                  <Button 
                    onClick={() => {
                      // Export to CSV
                      const headers = ['Name', 'Email', 'Phone', 'Session', 'Price', 'Payment Status', 'Payment Method', 'Date'];
                      const safeCell = (v) => {
                        const s = (v ?? '').toString();
                        return `"${s.replaceAll('"', '""')}"`;
                      };
                      const buildName = (r) => {
                        const fromFull = r.full_name || r.name;
                        if (fromFull && fromFull.toString().trim()) return fromFull.toString().trim();

                        // Back-compat for older exports: some rows may come back as first_name/last_name
                        // or nested inside a "contact" object.
                        const firstRaw = r.first_name ?? r.firstname ?? r.first ?? r.contact?.first_name;
                        const lastRaw = r.last_name ?? r.lastname ?? r.last ?? r.contact?.last_name;

                        const first = (firstRaw || '').toString().trim();
                        const last = (lastRaw || '').toString().trim();
                        const combined = `${first} ${last}`.trim();
                        return combined || '';
                      };
                      const csvData = registrations.map(r => [
                        safeCell(buildName(r)),
                        safeCell(r.email || ''),
                        safeCell(r.phone || ''),
                        safeCell(r.attending || ''),
                        safeCell(r.price_selected || 'free'),
                        safeCell(r.payment_status || 'pending'),
                        safeCell(r.payment_method || ''),
                        safeCell(new Date(r.created_at).toLocaleDateString())
                      ].join(','));
                      const csv = [headers.map(safeCell).join(','), ...csvData].join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `registrations-${new Date().toISOString().split('T')[0]}.csv`;
                      a.click();
                      toast.success('Exported registrations to CSV');
                    }}
                    variant="outline"
                    size="sm"
                    className="bg-stone-800 border-stone-700 text-white hover:border-teal-500/50"
                    style={{ borderRadius: 0 }}
                  >
                    📥 Export
                  </Button>
                </div>
              </div>

          <div className="bg-stone-900/50 border border-stone-800 rounded-lg overflow-x-auto">
            <Table className="bg-stone-900" style={{ backgroundColor: '#1c1917' }}>
              <TableHeader className="bg-stone-800/80 border-b border-stone-700" style={{ backgroundColor: '#292524' }}>
                <TableRow className="hover:bg-stone-800 border-b border-stone-700" style={{ backgroundColor: 'transparent' }}>
                  <TableHead className="font-bold w-12 text-amber-500">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === registrations.length && registrations.length > 0}
                      onChange={selectAllUsers}
                      className="cursor-pointer"
                    />
                  </TableHead>
                  <TableHead className="font-bold text-amber-500/80">Name</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Email</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Phone</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Session</TableHead>
                  <TableHead className="font-bold text-amber-500/80">In-Person</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Price</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Payment Status</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Payment Method</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Date</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Last Active</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Account Status</TableHead>
                  <TableHead className="font-bold text-amber-500/80">General Notes</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Scholarship Notes</TableHead>
                  <TableHead className="font-bold text-amber-500/80">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="bg-stone-900/50" style={{ backgroundColor: '#1c1917' }}>
                {filteredRegistrations.length === 0 ? (
                  <TableRow className="border-b border-stone-700" style={{ backgroundColor: '#1c1917' }}>
                    <TableCell colSpan={11} className="text-center py-12 text-stone-500">
                      No registrations yet
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRegistrations.map((reg) => (
                    <TableRow 
                      key={reg.id}
                      className="hover:bg-stone-800/50 transition-colors border-b border-stone-700/50 text-stone-300"
                      style={{ backgroundColor: '#1c1917' }}
                    >
                      {editingReg === reg.id ? (
                        // EDITING MODE - Show input fields
                        <>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(reg.email)}
                              onChange={() => toggleUserSelection(reg.email)}
                              className="cursor-pointer"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={editForm.full_name || ''}
                              onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                              className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                              style={{ borderRadius: 0 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="email"
                              value={editForm.email || ''}
                              onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                              className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                              style={{ borderRadius: 0 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={editForm.phone || ''}
                              onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                              className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                              style={{ borderRadius: 0 }}
                            />
                          </TableCell>
                          <TableCell>
                            <select
                              value={editForm.attending || 'intro'}
                              onChange={(e) => setEditForm({...editForm, attending: e.target.value})}
                              className="w-full p-2 border border-stone-700 bg-stone-800 text-white"
                              style={{ borderRadius: 0 }}
                            >
                              <option value="intro">Free Intro</option>
                              <option value="full_series">Full Series</option>
                              <option value="both">Both</option>
                              <option value="newsletter">Newsletter Only</option>
                              <option value="other">Other</option>
                              <option value="both">Both</option>
                            </select>
                          </TableCell>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={editForm.in_person || false}
                              onChange={(e) => setEditForm({...editForm, in_person: e.target.checked})}
                              className="cursor-pointer"
                            />
                          </TableCell>
                          <TableCell>
                            <select
                              value={editForm.price_selected || '175'}
                              onChange={(e) => setEditForm({...editForm, price_selected: e.target.value})}
                              className="w-full p-2 border border-stone-700 bg-stone-800 text-white"
                              style={{ borderRadius: 0 }}
                            >
                              <option value="free">Free (Intro Only)</option>
                              <option value="175">$175</option>
                              <option value="scholarship">Scholarship</option>
                              <option value="pay_forward">Pay It Forward</option>
                              <option value="newsletter">Newsletter Only</option>
                              <option value="other">Other</option>
                              <option value="pay_forward">Pay Forward</option>
                            </select>
                          </TableCell>
                          <TableCell>
                            <select
                              value={editForm.payment_status || 'pending'}
                              onChange={(e) => setEditForm({...editForm, payment_status: e.target.value})}
                              className="w-full p-2 border border-stone-700 bg-stone-800 text-white"
                              style={{ borderRadius: 0 }}
                            >
                              <option value="pending">Pending</option>
                              <option value="paid">Paid</option>
                              <option value="free">Free</option>
                            </select>
                          </TableCell>
                          <TableCell>
                            <select
                              value={editForm.payment_method || ''}
                              onChange={(e) => setEditForm({...editForm, payment_method: e.target.value})}
                              className="w-full p-2 border border-stone-700 bg-stone-800 text-white"
                              style={{ borderRadius: 0 }}
                            >
                              <option value="">Not Set</option>
                              <option value="paypal">PayPal</option>
                              <option value="cash">Cash</option>
                              <option value="check">Check</option>
                              <option value="credit_card">Credit Card</option>
                              <option value="free">Free (Intro)</option>
                            </select>
                          </TableCell>
                          <TableCell>{new Date(reg.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            {reg.last_login ? (
                              <div className="flex items-center gap-1 text-xs text-stone-400" title={new Date(reg.last_login).toLocaleString()}>
                                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                {new Date(reg.last_login).toLocaleDateString()}
                              </div>
                            ) : (
                              <span className="text-stone-600 text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {reg.account_created ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                ✓ Activated
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-stone-400">
                                ○ Not Activated
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => saveEdit(reg.id)}
                                className="text-green-600 hover:text-green-800"
                              >
                                Save
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingReg(null)}
                                className="text-stone-400 hover:text-gray-800"
                              >
                                Cancel
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(reg.email)}
                              onChange={() => toggleUserSelection(reg.email)}
                              className="cursor-pointer"
                            />
                          </TableCell>
                          <TableCell 
                            className="font-medium cursor-pointer hover:text-blue-600"
                            onClick={() => navigate(`/admin/user/${reg.id}`)}
                          >
                            {reg.full_name}
                            {(reg.tags && reg.tags.includes('ElchmE')) && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gradient-to-r from-amber-500 to-orange-600 text-black border border-amber-400/50 shadow-sm">
                                ✨ ElchmE
                              </span>
                            )}
                          </TableCell>
                          <TableCell 
                            className="cursor-pointer hover:text-blue-600"
                            onClick={() => navigate(`/admin/user/${reg.id}`)}
                          >
                            {reg.email}
                          </TableCell>
                          <TableCell>{reg.phone || '—'}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs font-semibold ${
                              (reg.attending || 'intro') === 'intro' ? 'bg-blue-100 text-blue-800' : 
                              (reg.attending || 'intro') === 'both' ? 'bg-purple-100 text-purple-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {(reg.attending || 'intro') === 'intro' ? 'Free Intro' : 
                               (reg.attending || 'intro') === 'both' ? 'Both' : 'Full Series'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {reg.in_person ? '✓' : '—'}
                          </TableCell>
                          <TableCell>
                            {reg.price_selected === 'scholarship' ? (
                              <span className="px-2 py-1 text-xs font-semibold bg-amber-500/20 text-amber-400">
                                Scholarship
                              </span>
                            ) : reg.price_selected === 'pay_forward' ? (
                              <span className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800">
                                Pay Forward
                              </span>
                            ) : reg.price_selected === 'free' ? (
                              <span className="px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800">
                                Free
                              </span>
                            ) : (
                              `$${reg.price_selected}`
                            )}
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs font-semibold ${
                              (reg.payment_status || 'pending') === 'paid' ? 'bg-green-100 text-green-800' :
                              (reg.payment_status || 'pending') === 'free' ? 'bg-gray-100 text-gray-800' :
                              'bg-orange-100 text-orange-800'
                            }`}>
                              {(reg.payment_status || 'pending').charAt(0).toUpperCase() + (reg.payment_status || 'pending').slice(1)}
                            </span>
                          </TableCell>
                          <TableCell className="capitalize">
                            {reg.payment_method ? reg.payment_method.replace('_', ' ') : '—'}
                          </TableCell>
                          <TableCell>{new Date(reg.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            {reg.last_login ? (
                              <div className="flex items-center gap-1 text-xs text-stone-400" title={new Date(reg.last_login).toLocaleString()}>
                                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                {new Date(reg.last_login).toLocaleDateString()}
                              </div>
                            ) : (
                              <span className="text-stone-600 text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {reg.account_created ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                ✓ Activated
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-stone-400">
                                ○ Not Activated
                              </span>
                            )}
                            {reg.attending === 'newsletter' && (
                              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                📧 Newsletter
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => startEditing(reg)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  if (window.confirm(`Create login account for ${reg.full_name}? They'll receive email with password: "password"`)) {
                                    try {
                                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/create-user-account/${reg.id}`, {
                                        method: 'POST',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      });
                                      if (response.ok) {
                                        const data = await response.json();
                                        toast.success(`Account created for ${reg.full_name}!`);
                                      } else {
                                        const errorData = await response.text();
                                        console.error('Create account failed:', errorData);
                                        toast.error(`Failed to create account: ${response.status}`);
                                      }
                                    } catch (error) {
                                      console.error('Create account error:', error);
                                      toast.error(`Error creating account: ${error.message}`);
                                    }
                                  }
                                }}
                                className="text-green-600 hover:text-green-800"
                                title="Create login account"
                              >
                                👤 Account
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(reg.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
            </div>
            </TabsContent>

            {/* Contact Submissions Tab */}
            <TabsContent value="contacts">
          <h3 className="text-2xl font-bold mb-6">Contact Form Submissions & Waiting List</h3>
          {contactSubmissions.length === 0 ? (
            <p className="text-stone-400">No contact submissions yet.</p>
          ) : (
            <div className="space-y-4">
              {contactSubmissions.map((contact) => (
                <Card key={contact.id} style={{ borderRadius: 0 }} className={
                  contact.type === 'waiting_list' ? 'border-2 border-yellow-400' : 
                  contact.type === 'scholarship_request' ? 'border-2 border-amber-500' : ''
                }>
                  <CardContent className="p-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <p className="font-bold text-lg">{contact.name}</p>
                          {contact.type === 'waiting_list' && (
                            <span className="px-3 py-1 text-xs font-semibold bg-amber-500/20 text-amber-400 rounded-full">
                              WAITING LIST
                            </span>
                          )}
                          {contact.type === 'scholarship_request' && (
                            <span className="px-3 py-1 text-xs font-semibold bg-amber-100 text-amber-800 rounded-full">
                              SCHOLARSHIP REQUEST
                            </span>
                          )}
                        </div>
                        <p className="text-stone-400 mb-1"><strong>Email:</strong> {contact.email}</p>
                        {contact.phone && <p className="text-stone-400 mb-1"><strong>Phone:</strong> {contact.phone}</p>}
                        <p className="text-xs text-stone-500 mt-2">Submitted: {new Date(contact.created_at).toLocaleString()}</p>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm"><strong>Type:</strong> <span className="capitalize">{contact.type?.replace('_', ' ')}</span></p>
                        {contact.interest && <p className="text-sm"><strong>Interest:</strong> {contact.interest}</p>}
                        {contact.contactPreference && <p className="text-sm"><strong>Contact Preference:</strong> {contact.contactPreference}</p>}
                      </div>
                    </div>
                    {contact.comments && (
                      <div className="mt-4 p-4 bg-stone-800/50 border-l-4 border-amber-500">
                        <p className="text-sm font-semibold mb-2">Comments:</p>
                        <p className="text-sm text-gray-700">{contact.comments}</p>
                      </div>
                    )}
                  </CardContent>
                  <div className="border-t p-4 flex justify-end gap-2">
                    {contact.newsletter_added ? (
                      <span className="px-3 py-2 text-sm font-semibold bg-green-100 text-green-800 rounded flex items-center">
                        <Mail className="w-4 h-4 mr-1" /> Added to Newsletter ✓
                      </span>
                    ) : (
                      <Button
                        onClick={async () => {
                          try {
                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/add-to-newsletter`, {
                              method: 'POST',
                              headers: { 
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}` 
                              },
                              body: JSON.stringify({
                                contact_id: contact.id,
                                email: contact.email,
                                name: contact.name
                              })
                            });
                            const responseText = await response.text();
                            let data;
                            try {
                              data = JSON.parse(responseText);
                            } catch {
                              throw new Error('Server error');
                            }
                            if (!response.ok) {
                              throw new Error(data.detail || 'Failed to add to newsletter');
                            }
                            toast.success(`Added ${contact.email} to newsletter list!`);
                            fetchContactSubmissions();
                            fetchRegistrations();
                          } catch (error) {
                            toast.error(error.message);
                          }
                        }}
                        variant="outline"
                        className="text-green-600 hover:bg-green-50 border-green-300"
                        style={{ borderRadius: 0 }}
                        data-testid={`add-newsletter-${contact.id}`}
                      >
                        <Mail className="w-4 h-4 mr-1" /> Add to Newsletter
                      </Button>
                    )}
                    <Button
                      onClick={async () => {
                        if (window.confirm(`Delete contact from ${contact.name}?`)) {
                          try {
                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contact/${contact.id}`, {
                              method: 'DELETE',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (response.ok) {
                              toast.success('Contact deleted');
                              fetchContactSubmissions();
                            }
                          } catch (error) {
                            toast.error('Failed to delete');
                          }
                        }
                      }}
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      style={{ borderRadius: 0 }}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages">
          <div>
            <h3 className="text-2xl font-bold mb-6">Messages</h3>
            <p className="text-stone-400">Message management coming soon...</p>
          </div>
        </TabsContent>

        {/* Send Email Tab */}
        <TabsContent value="email">
          {/* Onboarding Email System */}
          <Card className="bg-gradient-to-br from-rose-900/20 to-purple-900/20 border-rose-500/30 mb-6">
            <CardHeader>
              <CardTitle className="text-2xl font-light flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                <span className="text-2xl">🌱</span> User Onboarding Emails
              </CardTitle>
              <p className="text-stone-400 text-sm">Send beautiful welcome emails prompting users to login and set their password</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-stone-800/50 rounded-lg p-4 border border-amber-500/30">
                  <p className="text-xs text-amber-400 uppercase tracking-wide mb-1">Total Registered</p>
                  <p className="text-3xl font-bold text-white">{onboardingStats.total_registered}</p>
                </div>
                <div className="bg-stone-800/50 rounded-lg p-4 border border-emerald-500/30">
                  <p className="text-xs text-emerald-400 uppercase tracking-wide mb-1">Active Users</p>
                  <p className="text-3xl font-bold text-white">{onboardingStats.total_active}</p>
                </div>
                <div className="bg-stone-800/50 rounded-lg p-4 border border-rose-500/30">
                  <p className="text-xs text-rose-400 uppercase tracking-wide mb-1">Need Onboarding</p>
                  <p className="text-3xl font-bold text-white">{onboardingStats.needs_onboarding}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-6">
                <h4 className="text-white font-semibold mb-3">Send Onboarding Emails</h4>
                <p className="text-stone-300 text-sm mb-4">
                  These emails include login instructions, temporary password (password), and prompt users to create their own secure password.
                </p>
                
                <div className="flex gap-3">
                  {/* Test Email Button */}
                  <Button
                    onClick={sendTestOnboardingEmail}
                    disabled={sendingTestEmail}
                    className="bg-purple-600 hover:bg-purple-500 text-white"
                  >
                    {sendingTestEmail ? 'Sending...' : '📧 Send Test to macastat@gmail.com'}
                  </Button>
                  
                  {/* Mass Email Button */}
                  <Button
                    onClick={() => {
                      if (onboardingStats.needs_onboarding === 0) {
                        toast.info('No users need onboarding emails!');
                        return;
                      }
                      setShowMassEmailConfirm(true);
                    }}
                    disabled={sendingMassEmail}
                    className="bg-rose-600 hover:bg-rose-500 text-white"
                  >
                    {sendingMassEmail ? 'Sending...' : `✉️ Send to All (${onboardingStats.needs_onboarding} users)`}
                  </Button>
                  
                  {/* Refresh Stats Button */}
                  <Button
                    onClick={fetchOnboardingStats}
                    variant="outline"
                    className="border-stone-600 text-stone-300"
                  >
                    🔄 Refresh
                  </Button>
                </div>
              </div>

              {/* Email Preview */}
              <div className="bg-stone-800/30 border border-stone-700 rounded-lg p-4">
                <h4 className="text-white font-semibold mb-2 text-sm">Email Preview:</h4>
                <div className="text-xs text-stone-400 space-y-1">
                  <p>📧 Subject: "🌱 Your Belonging Sessions Account is Ready!"</p>
                  <p>✉️ From: hello@defiancesprings.com</p>
                  <p>💛 Includes: Login link, temporary password, setup instructions</p>
                  <p>🎨 Design: Beautiful gradient template with warm colors</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Mass Email Confirmation Dialog */}
          {showMassEmailConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowMassEmailConfirm(false)}>
              <Card className="bg-stone-900 border-rose-500/50 max-w-md" onClick={(e) => e.stopPropagation()}>
                <CardHeader>
                  <CardTitle className="text-white">⚠️ Confirm Mass Email</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-stone-300">
                    You're about to send onboarding emails to <strong className="text-rose-400">{onboardingStats.needs_onboarding} users</strong>.
                  </p>
                  <p className="text-stone-400 text-sm">
                    Each user will receive a personalized email with login instructions and be prompted to create their password.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={sendMassOnboardingEmail}
                      className="bg-rose-600 hover:bg-rose-500 text-white flex-1"
                    >
                      Yes, Send All Emails
                    </Button>
                    <Button
                      onClick={() => setShowMassEmailConfirm(false)}
                      variant="outline"
                      className="border-stone-600 text-stone-300 flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <h3 className="text-2xl font-bold mb-6">Send Custom Email to Users</h3>
          <Card style={{ borderRadius: 0 }}>
            <CardContent className="p-6">
              <form onSubmit={handleSendEmail} className="space-y-6">
                <div className="space-y-2">
                  <Label>To (email addresses - one per line or comma-separated)</Label>
                  <Textarea
                    required
                    value={emailData.to_emails}
                    onChange={(e) => setEmailData({...emailData, to_emails: e.target.value})}
                    placeholder="user@example.com&#10;another@example.com&#10;or: email1@test.com, email2@test.com"
                    className="border-black min-h-[100px]"
                    style={{ borderRadius: 0 }}
                  />
                  <p className="text-sm text-stone-400">Tip: You can copy emails from the registrations table</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input
                    required
                    value={emailData.subject}
                    onChange={(e) => setEmailData({...emailData, subject: e.target.value})}
                    placeholder="Email subject line"
                    className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                    style={{ borderRadius: 0 }}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea
                    required
                    value={emailData.message}
                    onChange={(e) => setEmailData({...emailData, message: e.target.value})}
                    placeholder="Type your message here..."
                    className="border-black min-h-[300px]"
                    style={{ borderRadius: 0 }}
                  />
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="html-email"
                      checked={emailData.is_html}
                      onChange={(e) => setEmailData({...emailData, is_html: e.target.checked})}
                    />
                    <Label htmlFor="html-email" className="font-normal">
                      Message contains HTML (for advanced formatting)
                    </Label>
                  </div>
                </div>
                
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> All emails will be sent from hello@defiancesprings.com with your custom message.
                  </p>
                </div>
                
                <Button 
                  type="submit" 
                  className="bg-amber-600 text-black hover:bg-black/90 px-8 py-4 text-lg"
                  disabled={sendingEmail}
                  style={{ borderRadius: 0 }}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feedback Tab */}
        <TabsContent value="feedback">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold">User Feedback & Testimonials</h3>
            <Button 
              onClick={fetchFeedback}
              variant="outline"
              className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
              style={{ borderRadius: 0 }}
              disabled={loadingFeedback}
            >
              {loadingFeedback ? 'Loading...' : 'Refresh'}
            </Button>
          </div>
          
          {feedbackList.length === 0 ? (
            <p className="text-stone-400">No feedback submitted yet.</p>
          ) : (
            <div className="space-y-4">
              {feedbackList.map((feedback) => (
                <Card key={feedback.id} style={{ borderRadius: 0 }} className={feedback.willing_testimonial ? 'border-2 border-green-500' : ''}>
                  <CardContent className="p-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <p className="font-bold text-lg">{feedback.user_name}</p>
                        <p className="text-stone-400">{feedback.user_email}</p>
                        <p className="text-sm text-stone-500">
                          {feedback.course_attended} • {new Date(feedback.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <h4 className="font-semibold">Ratings:</h4>
                        {Object.entries(feedback.ratings || {}).map(([key, value]) => (
                          value > 0 && (
                            <div key={key} className="flex justify-between text-sm">
                              <span className="capitalize">{key.replace('_', ' ')}</span>
                              <span className="font-bold">{value}/5</span>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                    
                    {feedback.message && (
                      <div className="mt-4 p-4 bg-stone-800/50 border-l-4 border-amber-500">
                        <h4 className="font-semibold mb-2">Comments:</h4>
                        <p className="text-gray-700">{feedback.message}</p>
                      </div>
                    )}
                    
                    {feedback.willing_testimonial && feedback.testimonial_text && (
                      <div className="mt-4 p-4 bg-green-50 border-l-4 border-green-600">
                        <h4 className="font-semibold mb-2 text-green-800">✨ Testimonial (Ready to Use):</h4>
                        <p className="text-gray-700 italic">"{feedback.testimonial_text}"</p>
                        <p className="text-sm text-stone-400 mt-2">— {feedback.testimonial_name}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Email Logs Tab */}
        <TabsContent value="logs">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold">Email Activity Logs</h3>
            <div className="flex gap-3">
              <div className="flex gap-2">
                <Button
                  onClick={() => setEmailLogFilter('all')}
                  variant={emailLogFilter === 'all' ? 'default' : 'outline'}
                  className={emailLogFilter === 'all' ? 'bg-amber-600 text-black' : 'border-black'}
                  style={{ borderRadius: 0 }}
                >
                  All
                </Button>
                <Button
                  onClick={() => setEmailLogFilter('success')}
                  variant={emailLogFilter === 'success' ? 'default' : 'outline'}
                  className={emailLogFilter === 'success' ? 'bg-green-600 text-white' : 'border-black'}
                  style={{ borderRadius: 0 }}
                >
                  Sent Mail
                </Button>
                <Button
                  onClick={() => setEmailLogFilter('failed')}
                  variant={emailLogFilter === 'failed' ? 'default' : 'outline'}
                  className={emailLogFilter === 'failed' ? 'bg-red-600 text-white' : 'border-black'}
                  style={{ borderRadius: 0 }}
                >
                  Failed
                </Button>
              </div>
              <Button 
                onClick={fetchEmailLogs}
                variant="outline"
                className="bg-stone-800 border-stone-700 text-white focus:border-amber-500"
                style={{ borderRadius: 0 }}
                disabled={loadingLogs}
              >
                {loadingLogs ? 'Loading...' : 'Refresh'}
              </Button>
            </div>
          </div>
          
          {emailLogs.filter(log => emailLogFilter === 'all' || log.status === emailLogFilter).length === 0 ? (
            <p className="text-stone-400">
              {emailLogFilter === 'success' ? 'No sent emails yet.' :
               emailLogFilter === 'failed' ? 'No failed emails (good news!)' :
               'No email logs yet. Logs will appear after emails are sent.'}
            </p>
          ) : (
            <div className="space-y-3">
              {emailLogs.filter(log => emailLogFilter === 'all' || log.status === emailLogFilter).map((log) => (
                <Card 
                  key={log.id} 
                  style={{ borderRadius: 0 }}
                  className={log.status === 'failed' ? 'border-2 border-red-500' : log.status === 'success' ? 'border-l-4 border-green-500' : ''}
                >
                  <CardContent className="p-4">
                    <div className="grid md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-stone-400">To</p>
                        <p className="font-semibold">{log.to_email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-stone-400">Subject</p>
                        <p className="font-semibold">{log.subject || '(No subject)'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-stone-400">Status</p>
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          log.status === 'success' ? 'bg-green-100 text-green-800' :
                          log.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {log.status.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm text-stone-400">Time</p>
                        <p className="text-sm">{new Date(log.timestamp).toLocaleString()}</p>
                      </div>
                    </div>
                    {log.error && (
                      <div className="mt-3 p-3 bg-red-50 border-l-4 border-red-500">
                        <p className="text-sm font-semibold text-red-800">Error:</p>
                        <p className="text-sm text-red-700">{log.error}</p>
                        {log.error_type && <p className="text-xs text-red-600 mt-1">Type: {log.error_type}</p>}
                      </div>
                    )}
                    {log.html_preview && log.status === 'success' && (
                      <div className="mt-3">
                        <details className="cursor-pointer">
                          <summary className="text-sm font-semibold text-gray-700 hover:text-black">View Email Content (Plain Text)</summary>
                          <div className="mt-2 p-3 bg-stone-800/50 border border-gray-200 rounded">
                            <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                              {log.html_preview.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()}
                            </pre>
                          </div>
                        </details>
                      </div>
                    )}
                    {log.smtp_host && (
                      <div className="mt-2">
                        <p className="text-xs text-stone-500">SMTP Host: {log.smtp_host}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="mt-0">
          <div className="bg-stone-900/80 rounded-xl border border-stone-800 p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-light text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  📊 Reports & Analytics
                </h2>
                <p className="text-sm text-stone-500">Course enrollment, payments, and participation data</p>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid md:grid-cols-4 gap-4 mb-8">
              <div className="bg-stone-800/50 border border-stone-700 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-amber-500" />
                  <span className="text-stone-400 text-sm">Total Enrolled</span>
                </div>
                <p className="text-3xl font-light text-white">{reportsFilteredRegistrations.length}</p>
                <p className="text-xs text-stone-500 mt-1">
                  {reportsCourseFilter === 'all' ? 'Excluding newsletter only' : `${reportsCourseFilter === 'intro' ? 'Free Intro' : reportsCourseFilter === 'full_series' ? 'Full Series' : 'Both'} only`}
                </p>
              </div>
              
              <div className="bg-stone-800/50 border border-stone-700 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="w-5 h-5 text-emerald-500" />
                  <span className="text-stone-400 text-sm">Total Revenue</span>
                </div>
                <p className="text-3xl font-light text-emerald-400">
                  ${reportsFilteredRegistrations
                    .filter(r => r.payment_status === 'paid')
                    .reduce((sum, r) => {
                      const price = r.price_selected;
                      if (!price || price === 'free' || price === 'scholarship' || price === 'pay_forward') return sum;
                      const numPrice = parseFloat(price);
                      return sum + (isNaN(numPrice) ? 0 : numPrice);
                    }, 0)
                    .toFixed(2)}
                </p>
                <p className="text-xs text-stone-500 mt-1">From paid registrations</p>
              </div>
              
              <div className="bg-stone-800/50 border border-stone-700 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  <span className="text-stone-400 text-sm">Full Series</span>
                </div>
                <p className="text-3xl font-light text-white">{reportsFilteredRegistrations.filter(r => r.attending === 'full_series' || r.attending === 'both').length}</p>
                <p className="text-xs text-stone-500 mt-1">Eight-part course</p>
              </div>
              
              <div className="bg-stone-800/50 border border-stone-700 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <BarChart3 className="w-5 h-5 text-purple-500" />
                  <span className="text-stone-400 text-sm">Paid Rate</span>
                </div>
                <p className="text-3xl font-light text-white">
                  {reportsFilteredRegistrations.filter(r => r.attending !== 'intro').length > 0 
                    ? Math.round((reportsFilteredRegistrations.filter(r => r.payment_status === 'paid' && r.attending !== 'intro').length / 
                        reportsFilteredRegistrations.filter(r => r.attending !== 'intro').length) * 100)
                    : 0}%
                </p>
                <p className="text-xs text-stone-500 mt-1">Series enrollments paid</p>
              </div>
            </div>

            {/* Course Filter */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-white mb-4">Enrollment by Course</h3>
              <div className="flex gap-2 flex-wrap mb-4">
                <button 
                  onClick={() => setReportsCourseFilter('all')}
                  className={`px-3 py-1.5 border rounded text-sm transition-all ${
                    reportsCourseFilter === 'all' 
                      ? 'bg-amber-500/20 text-amber-400 border-amber-500/50' 
                      : 'bg-stone-800 text-stone-400 border-stone-700 hover:border-amber-500/30'
                  }`}
                >
                  All Courses: {registrations.filter(r => r.attending !== 'newsletter').length}
                </button>
                <button 
                  onClick={() => setReportsCourseFilter('intro')}
                  className={`px-3 py-1.5 border rounded text-sm transition-all ${
                    reportsCourseFilter === 'intro' 
                      ? 'bg-blue-500/20 text-blue-400 border-blue-500/50' 
                      : 'bg-stone-800 text-stone-400 border-stone-700 hover:border-blue-500/30'
                  }`}
                >
                  Free Intro: {registrations.filter(r => r.attending === 'intro').length}
                </button>
                <button 
                  onClick={() => setReportsCourseFilter('full_series')}
                  className={`px-3 py-1.5 border rounded text-sm transition-all ${
                    reportsCourseFilter === 'full_series' 
                      ? 'bg-purple-500/20 text-purple-400 border-purple-500/50' 
                      : 'bg-stone-800 text-stone-400 border-stone-700 hover:border-purple-500/30'
                  }`}
                >
                  Full Series: {registrations.filter(r => r.attending === 'full_series').length}
                </button>
                <button 
                  onClick={() => setReportsCourseFilter('both')}
                  className={`px-3 py-1.5 border rounded text-sm transition-all ${
                    reportsCourseFilter === 'both' 
                      ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' 
                      : 'bg-stone-800 text-stone-400 border-stone-700 hover:border-emerald-500/30'
                  }`}
                >
                  Both: {registrations.filter(r => r.attending === 'both').length}
                </button>
              </div>
              {reportsCourseFilter !== 'all' && (
                <p className="text-stone-500 text-sm mb-2">
                  Showing {reportsFilteredRegistrations.length} registrations for "{reportsCourseFilter === 'intro' ? 'Free Intro' : reportsCourseFilter === 'full_series' ? 'Full Series' : 'Both'}"
                </p>
              )}
            </div>

            {/* Payment Summary Table */}
            <div className="mb-8">
              <h3 className="text-lg font-medium text-white mb-4">
                Payment Summary 
                <span className="text-stone-500 text-sm font-normal ml-2">
                  ({reportsFilteredRegistrations.length} registrations)
                </span>
              </h3>
              <div className="bg-stone-800/30 border border-stone-700 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader className="bg-stone-800/50">
                    <TableRow className="border-b border-stone-700">
                      <TableHead className="text-amber-500/80 font-bold">Name</TableHead>
                      <TableHead className="text-amber-500/80 font-bold">Email</TableHead>
                      <TableHead className="text-amber-500/80 font-bold">Course</TableHead>
                      <TableHead className="text-amber-500/80 font-bold">Amount Paid</TableHead>
                      <TableHead className="text-amber-500/80 font-bold">Payment Method</TableHead>
                      <TableHead className="text-amber-500/80 font-bold">Status</TableHead>
                      <TableHead className="text-amber-500/80 font-bold">Participating</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportsFilteredRegistrations.length === 0 ? (
                      <TableRow className="border-b border-stone-700/50">
                        <TableCell colSpan={7} className="text-center py-8 text-stone-500">
                          No registrations match the selected filter
                        </TableCell>
                      </TableRow>
                    ) : (
                      reportsFilteredRegistrations
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .slice(0, 50)
                        .map((reg) => (
                        <TableRow key={reg.id} className="border-b border-stone-700/50 hover:bg-stone-800/30">
                          <TableCell className="text-white font-medium">{reg.full_name}</TableCell>
                          <TableCell className="text-stone-400">{reg.email}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs ${
                              reg.attending === 'full_series' ? 'bg-purple-500/20 text-purple-300' :
                              reg.attending === 'both' ? 'bg-amber-500/20 text-amber-300' :
                              reg.attending === 'intro' ? 'bg-blue-500/20 text-blue-300' :
                              'bg-stone-700 text-stone-400'
                            }`}>
                              {reg.attending === 'full_series' ? 'Full Series' :
                               reg.attending === 'both' ? 'Both' :
                               reg.attending === 'intro' ? 'Free Intro' :
                               reg.attending}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`font-medium ${
                              reg.payment_status === 'paid' ? 'text-emerald-400' :
                              reg.price_selected === 'free' || reg.price_selected === 'scholarship' ? 'text-blue-400' :
                              'text-amber-400'
                            }`}>
                              {reg.price_selected === 'free' || reg.attending === 'intro' ? 'Free' :
                               reg.price_selected === 'scholarship' ? 'Scholarship' :
                               reg.price_selected === 'pay_forward' ? 'Pay Forward' :
                               reg.price_selected ? `$${reg.price_selected}` : 'Not set'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs ${
                              reg.payment_method === 'paypal' ? 'bg-blue-500/20 text-blue-300' :
                              reg.payment_method === 'credit_card' ? 'bg-purple-500/20 text-purple-300' :
                              reg.payment_method === 'cash' ? 'bg-emerald-500/20 text-emerald-300' :
                              reg.payment_method === 'check' ? 'bg-amber-500/20 text-amber-300' :
                              reg.payment_method === 'free' ? 'bg-stone-600 text-stone-300' :
                              'bg-stone-700 text-stone-500'
                            }`}>
                              {reg.payment_method ? reg.payment_method.replace('_', ' ').toUpperCase() : 'Not set'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs ${
                              reg.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-300' :
                              reg.payment_status === 'free' ? 'bg-blue-500/20 text-blue-300' :
                              'bg-amber-500/20 text-amber-300'
                            }`}>
                              {reg.payment_status === 'paid' ? '✓ Paid' :
                               reg.payment_status === 'free' ? 'Free' :
                               'Pending'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded text-xs ${
                              reg.last_active ? 'bg-emerald-500/20 text-emerald-300' : 'bg-stone-700 text-stone-500'
                            }`}>
                              {reg.last_active ? 'Active' : 'Not yet'}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Payment Method Breakdown */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-white mb-4">By Payment Method</h3>
                <div className="space-y-2">
                  {['paypal', 'credit_card', 'cash', 'check', 'free'].map(method => {
                    const count = registrations.filter(r => r.payment_method === method).length;
                    const total = registrations.filter(r => r.payment_method === method && r.price_selected && r.price_selected !== 'free')
                      .reduce((sum, r) => sum + (parseFloat(r.price_selected) || 0), 0);
                    return count > 0 ? (
                      <div key={method} className="flex items-center justify-between p-3 bg-stone-800/30 rounded border border-stone-700">
                        <span className="text-stone-300 capitalize">{method.replace('_', ' ')}</span>
                        <div className="text-right">
                          <span className="text-white font-medium">{count} users</span>
                          {total > 0 && <span className="text-emerald-400 ml-2">${total.toFixed(2)}</span>}
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-white mb-4">By Payment Status</h3>
                <div className="space-y-2">
                  {['paid', 'pending', 'free'].map(status => {
                    const count = registrations.filter(r => r.payment_status === status && r.attending !== 'newsletter').length;
                    return (
                      <div key={status} className="flex items-center justify-between p-3 bg-stone-800/30 rounded border border-stone-700">
                        <span className={`capitalize ${
                          status === 'paid' ? 'text-emerald-400' :
                          status === 'free' ? 'text-blue-400' :
                          'text-amber-400'
                        }`}>{status}</span>
                        <span className="text-white font-medium">{count} users</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Forum Admin Tab */}
        <TabsContent value="forum-admin">
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>Forum Moderation</h3>
              <Button onClick={() => { fetchForumStats(); fetchForumRooms(); fetchForumDiscussions(); fetchForumPosts(); }} variant="outline" className="border-stone-700">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </div>

            {/* Forum Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gradient-to-br from-teal-900/40 to-teal-800/20 border border-teal-500/30 rounded-lg p-4">
                <p className="text-teal-400 text-xs uppercase tracking-wider">Rooms</p>
                <p className="text-2xl font-bold text-white mt-1">{forumStats.total_rooms}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-900/40 to-blue-800/20 border border-blue-500/30 rounded-lg p-4">
                <p className="text-blue-400 text-xs uppercase tracking-wider">Discussions</p>
                <p className="text-2xl font-bold text-white mt-1">{forumStats.total_discussions}</p>
              </div>
              <div className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 border border-purple-500/30 rounded-lg p-4">
                <p className="text-purple-400 text-xs uppercase tracking-wider">Posts</p>
                <p className="text-2xl font-bold text-white mt-1">{forumStats.total_posts}</p>
              </div>
              <div className="bg-gradient-to-br from-amber-900/40 to-amber-800/20 border border-amber-500/30 rounded-lg p-4">
                <p className="text-amber-400 text-xs uppercase tracking-wider">Hidden</p>
                <p className="text-2xl font-bold text-white mt-1">{forumStats.hidden_posts}</p>
              </div>
              <div className="bg-gradient-to-br from-stone-800/40 to-stone-700/20 border border-stone-600/30 rounded-lg p-4">
                <a href="/forum" target="_blank" className="flex items-center gap-2 text-stone-400 hover:text-white transition-colors">
                  <ExternalLink className="w-4 h-4" />
                  <span className="text-sm">View Forum</span>
                </a>
              </div>
            </div>

            {/* Sub-tabs for Forum Admin */}
            <div className="flex gap-2 border-b border-stone-800 pb-2">
              {['overview', 'rooms', 'discussions', 'posts'].map(tab => (
                <button
                  key={tab}
                  onClick={() => {
                    setForumAdminTab(tab);
                    if (tab === 'rooms') fetchForumRooms();
                    else if (tab === 'discussions') fetchForumDiscussions();
                    else if (tab === 'posts') fetchForumPosts();
                    else fetchForumStats();
                  }}
                  className={`px-4 py-2 text-sm capitalize rounded-t transition-colors ${forumAdminTab === tab ? 'bg-teal-500/20 text-teal-400 border-b-2 border-teal-500' : 'text-stone-500 hover:text-white'}`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {forumAdminTab === 'overview' && (
              <div className="space-y-4">
                <h4 className="text-lg text-white">Recent Activity</h4>
                {forumStats.recent_posts?.length === 0 ? (
                  <p className="text-stone-500 text-center py-8">No recent posts</p>
                ) : (
                  <div className="space-y-2">
                    {forumStats.recent_posts?.map(post => (
                      <div key={post._id} className={`bg-stone-900/50 border rounded-lg p-3 ${post.hidden ? 'border-red-500/30 opacity-60' : 'border-stone-800'}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-white text-sm">{post.content}</p>
                            <p className="text-stone-500 text-xs mt-1">by {post.created_by_name} • {new Date(post.created_at).toLocaleString()}</p>
                          </div>
                          <div className="flex gap-2">
                            {post.hidden && <Badge className="bg-red-500/20 text-red-400">Hidden</Badge>}
                            <Button size="sm" variant="ghost" onClick={() => toggleForumItemVisibility('posts', post._id)} className="text-stone-500 hover:text-white h-7">
                              {post.hidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Rooms Tab */}
            {forumAdminTab === 'rooms' && (
              <div className="space-y-4">
                <h4 className="text-lg text-white">All Rooms ({forumRooms.length})</h4>
                <Table>
                  <TableHeader>
                    <TableRow className="border-stone-700">
                      <TableHead className="text-amber-500">Room Name</TableHead>
                      <TableHead className="text-amber-500">Created By</TableHead>
                      <TableHead className="text-amber-500">Discussions</TableHead>
                      <TableHead className="text-amber-500">Status</TableHead>
                      <TableHead className="text-amber-500">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {forumRooms.map(room => (
                      <TableRow key={room._id} className={`border-stone-800 ${room.hidden ? 'opacity-50' : ''}`}>
                        <TableCell className="text-white">{room.name}</TableCell>
                        <TableCell className="text-stone-400">{room.created_by_name}</TableCell>
                        <TableCell className="text-stone-400">{room.discussion_count}</TableCell>
                        <TableCell>
                          {room.hidden && <Badge className="bg-red-500/20 text-red-400 mr-1">Hidden</Badge>}
                          {room.locked && <Badge className="bg-amber-500/20 text-amber-400">Locked</Badge>}
                          {!room.hidden && !room.locked && <Badge className="bg-green-500/20 text-green-400">Active</Badge>}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => toggleForumItemVisibility('rooms', room._id)} title={room.hidden ? 'Show' : 'Hide'}>
                              {room.hidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteForumItem('rooms', room._id)} className="text-red-500 hover:text-red-400" title="Delete">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Discussions Tab */}
            {forumAdminTab === 'discussions' && (
              <div className="space-y-4">
                <h4 className="text-lg text-white">All Discussions ({forumDiscussions.length})</h4>
                <Table>
                  <TableHeader>
                    <TableRow className="border-stone-700">
                      <TableHead className="text-amber-500">Title</TableHead>
                      <TableHead className="text-amber-500">Author</TableHead>
                      <TableHead className="text-amber-500">Posts</TableHead>
                      <TableHead className="text-amber-500">Status</TableHead>
                      <TableHead className="text-amber-500">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {forumDiscussions.map(disc => (
                      <TableRow key={disc._id} className={`border-stone-800 ${disc.hidden ? 'opacity-50' : ''}`}>
                        <TableCell className="text-white max-w-xs truncate">{disc.title}</TableCell>
                        <TableCell className="text-stone-400">{disc.created_by_name}</TableCell>
                        <TableCell className="text-stone-400">{disc.post_count}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {disc.pinned && <Badge className="bg-blue-500/20 text-blue-400">📌 Pinned</Badge>}
                            {disc.locked && <Badge className="bg-amber-500/20 text-amber-400">🔒 Locked</Badge>}
                            {disc.hidden && <Badge className="bg-red-500/20 text-red-400">Hidden</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => toggleDiscussionPin(disc._id)} title={disc.pinned ? 'Unpin' : 'Pin'} className={disc.pinned ? 'text-blue-400' : ''}>
                              <Pin className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => toggleDiscussionLock(disc._id)} title={disc.locked ? 'Unlock' : 'Lock'} className={disc.locked ? 'text-amber-400' : ''}>
                              <Lock className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => toggleForumItemVisibility('discussions', disc._id)} title={disc.hidden ? 'Show' : 'Hide'}>
                              {disc.hidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteForumItem('discussions', disc._id)} className="text-red-500 hover:text-red-400" title="Delete">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Posts Tab */}
            {forumAdminTab === 'posts' && (
              <div className="space-y-4">
                <h4 className="text-lg text-white">Recent Posts ({forumPosts.length})</h4>
                <div className="space-y-2">
                  {forumPosts.map(post => (
                    <div key={post._id} className={`bg-stone-900/50 border rounded-lg p-4 ${post.hidden ? 'border-red-500/30 opacity-60' : 'border-stone-800'}`}>
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <p className="text-white text-sm">{post.content}</p>
                          <p className="text-stone-500 text-xs mt-2">
                            by <span className="text-stone-400">{post.created_by_name}</span> • {new Date(post.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {post.hidden && <Badge className="bg-red-500/20 text-red-400">Hidden</Badge>}
                          {post.flagged && <Badge className="bg-amber-500/20 text-amber-400">⚠️ Flagged</Badge>}
                          <Button size="sm" variant="ghost" onClick={() => toggleForumItemVisibility('posts', post._id)} title={post.hidden ? 'Show' : 'Hide'}>
                            {post.hidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteForumItem('posts', post._id)} className="text-red-500 hover:text-red-400" title="Delete">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <h3 className="text-2xl font-bold mb-6">Settings & Configuration</h3>
          
          <div className="space-y-8">
            
            {/* Interactive Exercise Manager */}
            <Card style={{ borderRadius: 0 }} className="border-2 border-blue-500">
              <CardHeader>
                <CardTitle className="text-xl">🎯 Interactive Exercise Manager</CardTitle>
                <p className="text-sm text-stone-400">Create exercises for pages 89, 90, 91-92 with AI formatting</p>
              </CardHeader>
              <CardContent>
                <ExerciseManager token={token} />
              </CardContent>
            </Card>

            {/* Homework Assignment Manager */}
            <Card style={{ borderRadius: 0 }} className="border-2 border-orange-500">
              <CardHeader>
                <CardTitle className="text-xl">📝 Homework Assignment Manager</CardTitle>
                <p className="text-sm text-stone-400">Add PDF homework content for each chapter</p>
              </CardHeader>
              <CardContent>
                <HomeworkManager token={token} />
              </CardContent>
            </Card>

            {/* Auto-Response Templates */}
            <Card style={{ borderRadius: 0 }} className="border-2 border-green-500">
              <CardHeader>
                <CardTitle className="text-xl">📧 Auto-Response Templates</CardTitle>
                <p className="text-sm text-stone-400">Automatically respond to contact form submissions</p>
              </CardHeader>
              <CardContent className="space-y-6">
                
                <div className="grid md:grid-cols-3 gap-4">
                  <Card style={{ borderRadius: 0 }} className="border border-blue-400">
                    <CardHeader>
                      <CardTitle className="text-base">Mailing List Response</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <Input
                          placeholder="Welcome to The Belonging Express!"
                          className="border-blue-500 text-sm"
                          style={{ borderRadius: 0 }}
                        />
                        <Textarea
                          placeholder="Thank you for joining The Belonging Express! We'll keep you updated..."
                          className="border-blue-500 text-sm min-h-[80px]"
                          style={{ borderRadius: 0 }}
                        />
                        <Button className="w-full bg-stone-700 text-white hover:bg-stone-600 text-sm" style={{ borderRadius: 0 }}>
                          Save Mailing List Response
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card style={{ borderRadius: 0 }} className="border border-green-400">
                    <CardHeader>
                      <CardTitle className="text-base">Contact Form Response</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <Input
                          placeholder="Thank you for reaching out!"
                          className="border-green-500 text-sm"
                          style={{ borderRadius: 0 }}
                        />
                        <Textarea
                          placeholder="Dear {{name}}, Thank you for your message. We've received your inquiry about..."
                          className="border-green-500 text-sm min-h-[80px]"
                          style={{ borderRadius: 0 }}
                        />
                        <Button className="w-full bg-amber-600 text-black hover:bg-amber-500 text-sm" style={{ borderRadius: 0 }}>
                          Save Contact Response
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </div>
          </TabsContent>

          {/* Sister Platforms Management Tab */}
          <TabsContent value="sister-platforms" className="flex-1 overflow-auto p-6">
            <SisterPlatformManager />
          </TabsContent>

          {/* Research Portal Tab */}
          <TabsContent value="research-portal" className="flex-1 overflow-auto p-6 space-y-6">
            <div className="max-w-4xl mx-auto">
              <div className="mb-6">
                <h2 className="text-2xl font-light mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  🔬 Research Portal Access
                </h2>
                <p className="text-stone-400">
                  Access the ElchmE.com Soul Score Research Portal to view and analyze wellness data.
                </p>
              </div>

              {/* ========== PRODUCTION PLATFORM CONNECTIVITY INDICATOR ========== */}
              <Card className="bg-gradient-to-br from-emerald-900/30 to-stone-900 border-emerald-500/40 mb-6" data-testid="platform-connectivity-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-light flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                      <div className="relative">
                        <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                        <div className="absolute inset-0 w-3 h-3 bg-emerald-400 rounded-full animate-ping opacity-75"></div>
                      </div>
                      Production Platform Connectivity
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                        LIVE
                      </Badge>
                      <Button
                        onClick={fetchPlatformConnectivity}
                        disabled={loadingConnectivity}
                        size="sm"
                        variant="ghost"
                        className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                        data-testid="refresh-connectivity"
                      >
                        <RefreshCw className={`w-4 h-4 ${loadingConnectivity ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Platform Status Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {/* Belonging Sessions - Hub */}
                    <div className="bg-stone-800/50 rounded-lg p-3 border border-amber-500/30 relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-amber-600"></div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                        <span className="text-xs font-medium text-amber-400">HUB</span>
                      </div>
                      <p className="text-sm font-semibold text-white truncate">Belonging Sessions</p>
                      <p className="text-xs text-stone-400 mt-1">
                        {platformConnectivity?.platforms?.belonging_sessions?.stats?.total_users || '—'} users
                      </p>
                    </div>

                    {/* ElchmE */}
                    <div className={`bg-stone-800/50 rounded-lg p-3 border relative overflow-hidden ${
                      platformConnectivity?.platforms?.elchme?.status === 'connected' 
                        ? 'border-emerald-500/30' 
                        : 'border-blue-500/30'
                    }`}>
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-red-500 to-emerald-500"></div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-2 h-2 rounded-full ${
                          platformConnectivity?.platforms?.elchme?.status === 'connected' 
                            ? 'bg-emerald-500' 
                            : 'bg-blue-400'
                        }`}></div>
                        <span className="text-xs font-medium text-blue-400">RESEARCH</span>
                      </div>
                      <p className="text-sm font-semibold text-white">
                        <span className="text-blue-400">E</span>
                        <span className="text-red-400">l</span>
                        <span className="text-emerald-400">c</span>
                        <span className="text-amber-400">h</span>
                        <span className="text-blue-400">m</span>
                        <span className="text-red-400">E</span>
                      </p>
                      <p className="text-xs text-stone-400 mt-1">
                        {platformConnectivity?.platforms?.elchme?.stats?.total_activities || 0} activities
                      </p>
                    </div>

                    {/* m.ai.ry */}
                    <div className={`bg-stone-800/50 rounded-lg p-3 border relative overflow-hidden ${
                      platformConnectivity?.platforms?.mairy?.status === 'connected' 
                        ? 'border-emerald-500/30' 
                        : 'border-purple-500/30'
                    }`}>
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500"></div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-2 h-2 rounded-full ${
                          platformConnectivity?.platforms?.mairy?.status === 'connected' 
                            ? 'bg-emerald-500' 
                            : 'bg-purple-400'
                        }`}></div>
                        <span className="text-xs font-medium text-purple-400">WISDOM</span>
                      </div>
                      <p className="text-sm font-semibold bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                        m.ai.ry
                      </p>
                      <p className="text-xs text-stone-400 mt-1">
                        {platformConnectivity?.platforms?.mairy?.stats?.total_conversations || 0} chats
                      </p>
                    </div>

                    {/* BlissAbode */}
                    <div className="bg-stone-800/50 rounded-lg p-3 border border-teal-500/30 relative overflow-hidden">
                      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-teal-500 to-cyan-500"></div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                        <span className="text-xs font-medium text-teal-400">WELLNESS</span>
                      </div>
                      <p className="text-sm font-semibold text-teal-300">BlissAbode</p>
                      <p className="text-xs text-amber-400 mt-1">Pending Setup</p>
                    </div>
                  </div>

                  {/* Connection Summary Bar */}
                  <div className="bg-stone-800/30 rounded-lg p-3 border border-stone-700/50">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-stone-500">Active Platforms:</span>
                          <span className="text-sm font-bold text-emerald-400">
                            {platformConnectivity?.summary?.active_platforms || 3}/{platformConnectivity?.summary?.total_platforms || 4}
                          </span>
                        </div>
                        <div className="w-px h-4 bg-stone-700"></div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-stone-500">Total Synced:</span>
                          <span className="text-sm font-bold text-amber-400">
                            {platformConnectivity?.summary?.total_synced_items || 0}
                          </span>
                        </div>
                        <div className="w-px h-4 bg-stone-700"></div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-stone-500">Last 24h:</span>
                          <span className="text-sm font-bold text-purple-400">
                            +{platformConnectivity?.summary?.synced_last_24h || 0}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-stone-500">
                        <span>Database:</span>
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">
                          {platformConnectivity?.database?.status || 'connected'}
                        </Badge>
                        <span className="text-emerald-400">Shared</span>
                      </div>
                    </div>
                  </div>

                  {/* Timestamp */}
                  {platformConnectivity?.timestamp && (
                    <div className="text-xs text-stone-500 text-right">
                      Status as of: {new Date(platformConnectivity.timestamp).toLocaleString()}
                    </div>
                  )}
                </CardContent>
              </Card>
              {/* ========== END PLATFORM CONNECTIVITY INDICATOR ========== */}

              {/* Research Portal Card */}
              <Card className="bg-gradient-to-br from-stone-900 to-black border-amber-500/20">
                <CardHeader>
                  <CardTitle className="text-xl font-light flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    <div className="p-2 rounded-lg bg-amber-500/20 text-amber-400">
                      🔬
                    </div>
                    ElchmE Research Portal
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Info Cards */}
                    <div className="bg-stone-800/30 border border-stone-700/50 rounded-lg p-4">
                      <div className="text-sm font-semibold text-amber-500 mb-2">📊 Soul Score Analytics</div>
                      <p className="text-xs text-stone-400">
                        View comprehensive Soul Score data, trends, and insights from ElchmE.com participants.
                      </p>
                    </div>
                    
                    <div className="bg-stone-800/30 border border-stone-700/50 rounded-lg p-4">
                      <div className="text-sm font-semibold text-amber-500 mb-2">🎯 Research Tools</div>
                      <p className="text-xs text-stone-400">
                        Access research dashboards, export data, and analyze wellness metrics.
                      </p>
                    </div>
                    
                    <div className="bg-stone-800/30 border border-stone-700/50 rounded-lg p-4">
                      <div className="text-sm font-semibold text-amber-500 mb-2">👥 Participant Data</div>
                      <p className="text-xs text-stone-400">
                        Review individual and aggregate participant information.
                      </p>
                    </div>
                    
                    <div className="bg-stone-800/30 border border-stone-700/50 rounded-lg p-4">
                      <div className="text-sm font-semibold text-amber-500 mb-2">🔐 Secure Access</div>
                      <p className="text-xs text-stone-400">
                        Single sign-on access with researcher credentials.
                      </p>
                    </div>
                  </div>

                  {/* Access Button */}
                  <div className="pt-4 border-t border-stone-700">
                    <a href="https://ElchmE.com/research" target="_blank" rel="noopener noreferrer">
                      <Button className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-black font-semibold py-6 rounded-lg">
                        <ExternalLink className="w-5 h-5 mr-2" />
                        Access Research Portal
                      </Button>
                    </a>
                    <p className="text-xs text-stone-500 mt-2 text-center">
                      Opens ElchmE.com Research Portal in a new tab
                    </p>
                  </div>

                  {/* Quick Actions */}
                  <div className="grid grid-cols-2 gap-3 pt-4">
                    <a href="https://ElchmE.com" target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="w-full border-stone-700 text-stone-400 hover:text-white hover:border-amber-500">
                        ElchmE Main Site
                      </Button>
                    </a>
                    <Button variant="outline" className="w-full border-stone-700 text-stone-400 hover:text-white hover:border-amber-500"  onClick={() => toast.info('Research documentation coming soon')}>
                      View Documentation
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Research Status */}
              <Card className="bg-stone-900/50 border-stone-800 mt-6">
                <CardHeader>
                  <CardTitle className="text-lg font-light">Integration Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-stone-400">ElchmE.com Connection</span>
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Connected</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-stone-400">SSO Integration</span>
                      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Ready</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-stone-400">Research Access</span>
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Enabled</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* SSO Integration Status Panel */}
              <Card className="bg-gradient-to-br from-stone-900 to-black border-purple-500/20 mt-6" data-testid="sso-status-panel">
                <CardHeader>
                  <CardTitle className="text-xl font-light flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                    <div className="p-2 rounded-lg bg-purple-500/20 text-purple-400">
                      🔗
                    </div>
                    SSO Integration Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* ElchmE SSO Status */}
                  <div className="bg-stone-800/30 border border-stone-700/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="font-light tracking-wide">
                          <span className="text-blue-400">E</span>
                          <span className="text-red-400">l</span>
                          <span className="text-emerald-400">c</span>
                          <span className="text-amber-400">h</span>
                          <span className="text-blue-400">m</span>
                          <span className="text-red-400">E</span>
                          <span className="text-stone-400">.com</span>
                        </span>
                        <Badge className={`${
                          ssoStatus.elchme.status === 'connected' 
                            ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                            : ssoStatus.elchme.status === 'error'
                            ? 'bg-red-500/20 text-red-400 border-red-500/30'
                            : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                        }`}>
                          {ssoStatus.elchme.status === 'connected' ? '✓ Connected' : 
                           ssoStatus.elchme.status === 'error' ? '✗ Error' : '○ Pending'}
                        </Badge>
                      </div>
                      <Button 
                        onClick={() => testSsoConnection('elchme')}
                        disabled={testingSso.elchme}
                        size="sm"
                        className="bg-amber-600 hover:bg-amber-500 text-black"
                        data-testid="test-elchme-sso"
                      >
                        {testingSso.elchme ? (
                          <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Testing...</>
                        ) : (
                          <><RefreshCw className="w-4 h-4 mr-1" /> Test Connection</>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-stone-400">{ssoStatus.elchme.message}</p>
                    {ssoStatus.elchme.lastTest && (
                      <p className="text-xs text-stone-500 mt-1">
                        Last tested: {new Date(ssoStatus.elchme.lastTest).toLocaleString()}
                      </p>
                    )}
                    <div className="mt-3 pt-3 border-t border-stone-700/50">
                      <p className="text-xs text-stone-400">
                        <span className="text-amber-400">Features:</span> Soul Score sync, Research data, Activity tracking
                      </p>
                    </div>
                  </div>

                  {/* m.ai.ry SSO Status */}
                  <div className="bg-stone-800/30 border border-stone-700/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="font-light tracking-wide bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
                          m.ai.ry
                        </span>
                        <span className="text-stone-400">.io</span>
                        <Badge className={`${
                          ssoStatus.mairy.status === 'connected' 
                            ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                            : ssoStatus.mairy.status === 'error'
                            ? 'bg-red-500/20 text-red-400 border-red-500/30'
                            : 'bg-purple-500/20 text-purple-400 border-purple-500/30'
                        }`}>
                          {ssoStatus.mairy.status === 'connected' ? '✓ Connected' : 
                           ssoStatus.mairy.status === 'error' ? '✗ Error' : '⏳ Pending Billing'}
                        </Badge>
                      </div>
                      <Button 
                        onClick={() => testSsoConnection('mairy')}
                        disabled={testingSso.mairy}
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-500 text-white"
                        data-testid="test-mairy-sso"
                      >
                        {testingSso.mairy ? (
                          <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Testing...</>
                        ) : (
                          <><RefreshCw className="w-4 h-4 mr-1" /> Test Connection</>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-stone-400">{ssoStatus.mairy.message}</p>
                    {ssoStatus.mairy.lastTest && (
                      <p className="text-xs text-stone-500 mt-1">
                        Last tested: {new Date(ssoStatus.mairy.lastTest).toLocaleString()}
                      </p>
                    )}
                    <div className="mt-3 pt-3 border-t border-stone-700/50">
                      <p className="text-xs text-stone-400">
                        <span className="text-purple-400">Features:</span> Wisdom conversations, AI insights, Chat history sync
                      </p>
                    </div>
                  </div>

                  {/* Database Sync Status */}
                  <div className="bg-stone-800/30 border border-emerald-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-emerald-400 font-semibold">🗄️ Shared MongoDB Database</span>
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Active</Badge>
                    </div>
                    <p className="text-xs text-stone-400">
                      All sites share the same database. User activities, Soul Scores, and conversations are automatically synced to the user's dashboard.
                    </p>
                    <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-stone-700/50">
                      <div className="text-center">
                        <div className="text-lg font-bold text-emerald-400">✓</div>
                        <div className="text-xs text-stone-500">ElchmE Sync</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-purple-400">⏳</div>
                        <div className="text-xs text-stone-500">m.ai.ry Sync</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-amber-400">✓</div>
                        <div className="text-xs text-stone-500">User Dashboard</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

        {/* LORE Tab */}
        <TabsContent value="lore">
          <LoreContentManager />
        </TabsContent>

        <TabsContent value="ebook">
          <div className="bg-stone-900/80 rounded-xl border border-stone-800 p-6">
            <EbookHighlightManager />
          </div>
        </TabsContent>

        <TabsContent value="announcements">
          <AnnouncementManager />
        </TabsContent>

        <TabsContent value="blog">
          <BlogPostManager token={token} />
        </TabsContent>

        <TabsContent value="resources">
          <ResourcesManager token={token} />
        </TabsContent>

        <TabsContent value="videos">
          <VideoManager />
        </TabsContent>

        <TabsContent value="experimental">
          <ExperimentalFeatures />
        </TabsContent>

          </section>
        </main>
      </div>

      </Tabs>
    </div>
  );
}

export default Admin;



